// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
    baseUrl: `${string}://${string}/api/v1` | (string & {});
};

/**
 * Whether a configured record is in use. Inactive records are retained for history rather than deleted.
 *
 */
export const ResourceState = { active: 'ACTIVE', inactive: 'INACTIVE' } as const;

/**
 * Whether a configured record is in use. Inactive records are retained for history rather than deleted.
 *
 */
export type ResourceState = typeof ResourceState[keyof typeof ResourceState];

/**
 * Lifecycle state of an asset. INACTIVE assets were marked stale after 90 days unseen or set by a user, and return to ACTIVE when seen again. DUPLICATE assets are another record of the same device as another asset, optionally named by duplicate_of_asset_id.
 */
export const AssetState = {
    active: 'ACTIVE',
    inactive: 'INACTIVE',
    duplicate: 'DUPLICATE'
} as const;

/**
 * Lifecycle state of an asset. INACTIVE assets were marked stale after 90 days unseen or set by a user, and return to ACTIVE when seen again. DUPLICATE assets are another record of the same device as another asset, optionally named by duplicate_of_asset_id.
 */
export type AssetState = typeof AssetState[keyof typeof AssetState];

/**
 * How the asset type was determined. When sources disagree, the stronger method wins, from weakest to strongest: NETWORK_ACTIVITY_PORT_INFERRED, HOSTNAME_INFERRED, PROTOCOL_SELF_REPORTED, ENDPOINT_SENSOR_CONFIG.
 */
export const AssetTypeDeterminationMethod = {
    network_activity_port_inferred: 'NETWORK_ACTIVITY_PORT_INFERRED',
    hostname_inferred: 'HOSTNAME_INFERRED',
    protocol_self_reported: 'PROTOCOL_SELF_REPORTED',
    endpoint_sensor_config: 'ENDPOINT_SENSOR_CONFIG'
} as const;

/**
 * How the asset type was determined. When sources disagree, the stronger method wins, from weakest to strongest: NETWORK_ACTIVITY_PORT_INFERRED, HOSTNAME_INFERRED, PROTOCOL_SELF_REPORTED, ENDPOINT_SENSOR_CONFIG.
 */
export type AssetTypeDeterminationMethod = typeof AssetTypeDeterminationMethod[keyof typeof AssetTypeDeterminationMethod];

/**
 * How the asset OS was determined. When sources disagree, the stronger method wins, from weakest to strongest: GAL4T_INFERRED, ENDPOINT_SENSOR_CONFIG.
 */
export const AssetOsDeterminationMethod = { gal4t_inferred: 'GAL4T_INFERRED', endpoint_sensor_config: 'ENDPOINT_SENSOR_CONFIG' } as const;

/**
 * How the asset OS was determined. When sources disagree, the stronger method wins, from weakest to strongest: GAL4T_INFERRED, ENDPOINT_SENSOR_CONFIG.
 */
export type AssetOsDeterminationMethod = typeof AssetOsDeterminationMethod[keyof typeof AssetOsDeterminationMethod];

/**
 * A manual asset state change. duplicate_of_asset_id is optional for DUPLICATE and must be omitted or null otherwise.
 */
export type AssetStateInput = {
    state: AssetState;
    duplicate_of_asset_id?: string | null;
};

/**
 * Offline when the asset carried no traffic within the last bucket_hours; decided first, so something silent right now reads offline even if it is usually up. Otherwise intermittent when it was active in fewer than half the blocks of its own monitored span, and online when it wasn't. Both tests are measured in blocks at the requested bucket_hours, so the value answers the question the caller asked: an asset that talks once a day is online by day and intermittent by hour. Computed server-side so the status filter and the displayed value come from one rule.
 */
export const AssetActivityStatus = {
    online: 'online',
    offline: 'offline',
    intermittent: 'intermittent'
} as const;

/**
 * Offline when the asset carried no traffic within the last bucket_hours; decided first, so something silent right now reads offline even if it is usually up. Otherwise intermittent when it was active in fewer than half the blocks of its own monitored span, and online when it wasn't. Both tests are measured in blocks at the requested bucket_hours, so the value answers the question the caller asked: an asset that talks once a day is online by day and intermittent by hour. Computed server-side so the status filter and the displayed value come from one rule.
 */
export type AssetActivityStatus = typeof AssetActivityStatus[keyof typeof AssetActivityStatus];

/**
 * Whether a tag applies to an asset. Removing a tag suppresses it rather than deleting the row, so automatic tagging will not re-apply a tag a user removed.
 *
 */
export const AssetToTagState = { active: 'ACTIVE', suppressed: 'SUPPRESSED' } as const;

/**
 * Whether a tag applies to an asset. Removing a tag suppresses it rather than deleting the row, so automatic tagging will not re-apply a tag a user removed.
 *
 */
export type AssetToTagState = typeof AssetToTagState[keyof typeof AssetToTagState];

/**
 * The value type a custom asset attribute holds.
 */
export const CustomAssetAttributeType = {
    string: 'STRING',
    integer: 'INTEGER',
    date: 'DATE'
} as const;

/**
 * The value type a custom asset attribute holds.
 */
export type CustomAssetAttributeType = typeof CustomAssetAttributeType[keyof typeof CustomAssetAttributeType];

/**
 * An asset column that may be enumerated for distinct values.
 */
export const AssetDistinctValuesField = {
    state: 'state',
    os: 'os',
    hardware_vendor: 'hardware_vendor',
    agent_type: 'agent_type',
    agent_version: 'agent_version',
    observers: 'observers',
    building_name: 'building_name',
    mac_vendor: 'mac_vendor',
    os_name: 'os_name',
    os_version: 'os_version',
    os_build: 'os_build',
    model_number: 'model_number',
    mac_created_by_log_source: 'mac_created_by_log_source',
    mac_last_updated_by_log_source: 'mac_last_updated_by_log_source',
    ip_created_by_log_source: 'ip_created_by_log_source',
    ip_last_updated_by_log_source: 'ip_last_updated_by_log_source',
    finding_hostname: 'finding_hostname'
} as const;

/**
 * An asset column that may be enumerated for distinct values.
 */
export type AssetDistinctValuesField = typeof AssetDistinctValuesField[keyof typeof AssetDistinctValuesField];

/**
 * Dynamic query parameters for filtering. A key is a column name, optionally suffixed with `__operator` (`ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull`, `isnotnull`); it may reference a joined table with `.`, and may OR the same value across several fields by separating them with `|`. A scalar value is compared directly. An array value matches any of its entries, or — for the negated operators — all of them.
 *
 * The reserved keys `_OR` and `_AND` take arrays of nested filter objects. They are supported by the API but not modelled here, because expressing the recursion would complicate every generated client for a form no current consumer uses.
 *
 */
export type Queryables = {
    [key: string]: string | number | boolean | null | Array<string | number | boolean | null>;
};

export type PatchRequest = Array<JsonPatchRequestAddReplaceTest | JsonPatchRequestRemove | JsonPatchRequestMoveCopy>;

export type JsonPatchRequestAddReplaceTest = {
    /**
     * A JSON Pointer path.
     */
    path: string;
    /**
     * The value to add, replace or test.
     */
    value: unknown;
    /**
     * The operation to perform.
     */
    op: 'add' | 'replace' | 'test';
};

export type JsonPatchRequestRemove = {
    /**
     * A JSON Pointer path.
     */
    path: string;
    /**
     * The operation to perform.
     */
    op: 'remove';
};

export type JsonPatchRequestMoveCopy = {
    /**
     * A JSON Pointer path.
     */
    path: string;
    /**
     * The operation to perform.
     */
    op: 'move' | 'copy';
    /**
     * A JSON Pointer path.
     */
    from: string;
};

/**
 * Relates findings <-> assets
 */
export type Aafh = {
    finding_id: string;
    asset_id: string;
};

/**
 * A detection
 */
export type Ads = {
    id: string;
    version: string;
    title: string;
    summary: string;
    categorization: string;
    strategy_abstract: string;
    technical_context: string;
    blind_spots_and_assumptions: string;
    false_positives?: string;
    severity: string;
    validation: string;
    response: string;
    additional_resources: string;
    context_fields: Array<string>;
    event_header_fields: Array<string>;
};

/**
 * Virtual Routing and Forwarding entry used for asset IP reuse merging logic
 */
export type Vrf = {
    id: number;
    name: string;
    description?: string | null;
    state: ResourceState;
    created_by?: string | null;
    last_modified_by?: string | null;
    timestamp?: Date;
    /**
     * Tag Fixer applies to an asset in this VRF whose VLAN could not be confirmed. Null disables VLAN-unknown tagging for this VRF.
     */
    unknown_vlan_tag_id?: number | null;
};

/**
 * Input schema for creating a VRF
 */
export type VrfInput = {
    name: string;
    description?: string | null;
    state?: ResourceState;
    created_by?: string | null;
    last_modified_by?: string | null;
    unknown_vlan_tag_id?: number | null;
};

/**
 * A CIDR network prefix associated with a VRF
 */
export type NetworkPrefix = {
    id: number;
    prefix: string;
    vrf_id: number;
    /**
     * The VLAN this prefix is scoped to, if any (references a vlans row). Null means the prefix applies regardless of VLAN.
     */
    vlan_id?: number | null;
    name: string;
    description?: string | null;
    state: ResourceState;
    created_by?: string | null;
    last_modified_by?: string | null;
    timestamp?: Date;
};

/**
 * Input schema for creating a NetworkPrefix
 */
export type NetworkPrefixInput = {
    prefix: string;
    vrf_id: number;
    vlan_id?: number | null;
    name: string;
    description?: string | null;
    state?: ResourceState;
    created_by?: string | null;
    last_modified_by?: string | null;
};

/**
 * A VLAN used to disambiguate IP re-use within a single VRF
 */
export type Vlan = {
    id: number;
    /**
     * The 802.1Q VLAN ID (0-4095)
     */
    vlan_id: number;
    vrf_id: number;
    name: string;
    description?: string | null;
    state: ResourceState;
    created_by?: string | null;
    last_modified_by?: string | null;
    timestamp?: Date;
};

/**
 * Input schema for creating a VLAN
 */
export type VlanInput = {
    vlan_id: number;
    vrf_id: number;
    name: string;
    description?: string | null;
    state?: ResourceState;
    created_by?: string | null;
    last_modified_by?: string | null;
};

/**
 * Junction record associating a Galvanick appliance with a local network prefix
 */
export type NetworkSensorLocalPrefix = {
    id: number;
    network_sensor_id: number;
    local_network_prefix_id: number;
    vrf_id: number;
    created_by?: string | null;
    last_modified_by?: string | null;
};

/**
 * A physical building associated with Galvanick appliances and assets
 */
export type Building = {
    id: number;
    name: string;
    description?: string | null;
    state: ResourceState;
    created_by?: string | null;
    last_modified_by?: string | null;
    timestamp?: Date;
};

/**
 * Input schema for creating a Building
 */
export type BuildingInput = {
    name: string;
    description?: string | null;
    state?: ResourceState;
    created_by?: string | null;
    last_modified_by?: string | null;
};

/**
 * An admin-defined custom asset attribute field. attribute_type is immutable once created.
 */
export type CustomAssetAttributeDefinition = {
    id: number;
    name: string;
    attribute_type: CustomAssetAttributeType;
    created_by?: string | null;
    last_modified_by?: string | null;
    timestamp?: Date;
};

/**
 * Input schema for creating a CustomAssetAttributeDefinition
 */
export type CustomAssetAttributeDefinitionInput = {
    name: string;
    attribute_type: CustomAssetAttributeType;
    created_by?: string | null;
    last_modified_by?: string | null;
};

/**
 * Input schema for requesting a presigned pcap upload URL.
 */
export type PcapUploadPresignInput = {
    file_name: string;
    /**
     * User-entered label standing in for a real network sensor hostname, included in the staging S3 key so the raw upload can be traced back to its intended appliance even if processing never runs.
     */
    network_sensor_name: string;
};

/**
 * A presigned S3 PUT URL for uploading a pcap file, and the key it was signed for.
 */
export type PcapUploadPresignOutput = {
    upload_url: string;
    s3_key: string;
};

/**
 * Input schema for processing an already-uploaded pcap file.
 */
export type PcapUploadProcessInput = {
    s3_key: string;
    /**
     * User-entered label standing in for a real network sensor hostname in the resulting logs' S3 key, since there's no real network sensor here.
     */
    network_sensor_name: string;
};

/**
 * A custom attribute's value on an asset, with its definition's name and type. Filter assets on it with `custom_attr_<definition_id>`; see Filtering.
 *
 */
export type AssetCustomAttribute = {
    /**
     * The CustomAssetAttributeDefinition id; stable across renames.
     */
    definition_id: number;
    name: string;
    attribute_type: CustomAssetAttributeType;
    /**
     * A string for STRING, an integer for INTEGER, or an RFC 3339 timestamp string for DATE.
     */
    value: string | number;
};

/**
 * A single custom attribute value to set for an asset. A null value clears that attribute.
 */
export type AssetCustomAttributeValueInput = {
    value: unknown;
    last_modified_by?: string | null;
};

/**
 * A row of asset_custom_attribute_values.
 */
export type AssetCustomAttributeValues = {
    asset_id: string;
    values: {
        [key: string]: unknown;
    };
    last_modified_by?: string | null;
    last_modified_timestamp?: Date;
};

/**
 * A Galvanick appliance used for ingesting log data
 */
export type GalvanickAppliance = {
    id: number;
    name: string;
    vrf_id: number;
    building_id?: number | null;
    description?: string | null;
    state: ResourceState;
    uploading_winlogbeat?: boolean;
    uploading_suricata?: boolean;
    uploading_zeek?: boolean;
    created_by?: string | null;
    last_modified_by?: string | null;
    timestamp?: Date;
    local_prefixes?: Array<NetworkSensorLocalPrefix>;
};

/**
 * Input schema for creating a GalvanickAppliance
 */
export type GalvanickApplianceInput = {
    name: string;
    vrf_id: number;
    building_id?: number | null;
    description?: string | null;
    state?: ResourceState;
    uploading_winlogbeat?: boolean;
    uploading_suricata?: boolean;
    uploading_zeek?: boolean;
    created_by?: string | null;
    last_modified_by?: string | null;
};

export type EndpointSensorLogSource = {
    /**
     * Key of a Windows event log channel in endpoint_log_sources, e.g. SECURITY.
     */
    log_source: string;
    label: string;
    /**
     * Hours without an event before the sensor shows Data missing, from the channel's EndpointLogSource override or else its default. Null never flags this source.
     */
    missing_after_hours: number | null;
    /**
     * Null when this sensor hasn't sent from this channel since tracking began.
     */
    last_seen_timestamp: Date | null;
    /**
     * When tracking this channel on this sensor began: the later of the channel's first sighting in the environment and the sensor's first upload. A never-seen channel counts toward Data missing from here, since there is no history from before.
     */
    tracked_since: Date;
};

export type EndpointLogSource = {
    /**
     * Key of the channel, e.g. SECURITY.
     */
    log_source: string;
    /**
     * The Windows event log channel, e.g. Microsoft-Windows-Sysmon/Operational.
     */
    log_name: string;
    label: string;
    /**
     * An admin's override of hours without an event before Data missing. 0 never flags this channel; null uses default_missing_after_hours.
     */
    missing_after_hours: number | null;
    /**
     * The portal's built-in threshold for this channel. Null never flags it.
     */
    readonly default_missing_after_hours: number | null;
};

export type EndpointLogSourceInput = {
    log_source: string;
    /**
     * 0 never flags this channel; null returns it to its default.
     */
    missing_after_hours: number | null;
};

export type EndpointSensorUploader = {
    network_sensor_name: string;
    /**
     * Oldest event seen through this network sensor since the refresh began tracking.
     */
    first_seen_timestamp: Date;
    last_seen_timestamp: Date;
};

/**
 * An endpoint sensor we installed, registered automatically by fixer on the sensor's first upload, joined to the asset it runs on. Only network_sensor_id and state are writable: the rest are fixer's or the asset's, and the Description is the asset's user_description, edited via PATCH /assets/{id} rather than duplicated here.
 */
export type EndpointSensor = {
    id: number;
    /**
     * The machine this sensor runs on. One registration per asset.
     */
    readonly asset_id?: string;
    /**
     * The sensor's own uid as at registration. Not the primary key: machines cloned from an image share a uid, so keying on it would merge them into one row. Differing from the asset's current agent_id means the sensor was reinstalled.
     */
    readonly agent_id?: string | null;
    /**
     * Appliance this sensor is supposed to upload through. Intent, deliberately not reported_via -- a gap between the two is a misconfigured sensor. Defaulted to the observer that delivered the first upload.
     */
    network_sensor_id?: number | null;
    /**
     * OS at install time, kept so drift from reported_os is visible.
     */
    readonly installed_os?: string | null;
    /**
     * Sensor version at install time, kept so drift from reported_agent_version is visible.
     */
    readonly installed_agent_version?: string | null;
    /**
     * INACTIVE means the install was deliberately retired and must not alarm.
     */
    state: 'ACTIVE' | 'INACTIVE';
    last_modified_by?: string | null;
    readonly last_modified_timestamp?: Date;
    readonly timestamp?: Date;
    /**
     * The asset's user_description, shown as "Description" in the portal. The one readable label for this machine; edit it on the asset.
     */
    readonly description?: string | null;
    readonly hostname?: string | null;
    /**
     * The asset's type, taken from its user_type when someone has overridden it and from the detected type otherwise, so filtering on it matches what is displayed. See Asset.type for the supported values.
     */
    readonly type?: string;
    readonly network_sensor_name?: string | null;
    readonly most_recent_ip?: string | null;
    readonly reported_os?: string | null;
    readonly reported_agent_version?: string | null;
    /**
     * Newest event from any of this sensor's log sources in the datalake, refreshed every 5 minutes, or the sensor's last upload recorded by fixer until the history search finds a source.
     */
    readonly last_upload_timestamp?: Date | null;
    /**
     * Every Windows log channel seen from endpoint sensors, sorted by label, with the newest event seen from this sensor's agent, refreshed every 5 minutes from the datalake.
     */
    readonly log_sources?: Array<EndpointSensorLogSource>;
    /**
     * Appliances that actually observed the host, to compare against network_sensor_id.
     */
    readonly reported_via?: Array<string> | null;
    /**
     * Network sensors that forwarded this sensor's events, newest first, refreshed every 5 minutes from the datalake. Empty until the refresh has seen the agent.
     */
    readonly uploaded_via?: Array<EndpointSensorUploader>;
    /**
     * First winlogbeat upload seen from this sensor. Null until it first uploads.
     */
    readonly first_winlogbeat_upload_timestamp?: Date | null;
    /**
     * The asset's tags, which is where a machine's physical location is recorded.
     */
    readonly tags?: Array<AssetToTag>;
};

/**
 * Represents a closure category (True Positive or False Positive)
 */
export type ClosureCategory = {
    id: string;
    name: string;
    description?: string | null;
};

/**
 * A set of values for a single filter_map key
 */
export type SuppressionValues = {
    values?: Array<string>;
};

/**
 * A suppression rule returned by the server
 */
export type SuppressionRecord = {
    id?: string;
    detection_id?: string;
    updated_by?: string;
    reason?: string;
    condition_operator?: string;
    state?: 'ACTIVE' | 'INACTIVE';
    suppress_all?: boolean;
    filter_map?: {
        [key: string]: SuppressionValues;
    };
    expires_at?: Date | null;
};

/**
 * Fields the caller provides when creating or updating a suppression rule
 */
export type SuppressionInput = {
    /**
     * Omit when creating; include to update an existing rule
     */
    id?: string;
    detection_id?: string;
    updated_by?: string;
    reason?: string;
    condition_operator?: string;
    state?: 'ACTIVE' | 'INACTIVE';
    suppress_all?: boolean;
    filter_map?: {
        [key: string]: SuppressionValues;
    };
    expires_at?: Date | null;
};

/**
 * Represents an enumerated closure code
 */
export type ClosureCode = {
    id: string;
    name: string;
    description?: string | null;
    category_id?: string | null;
};

/**
 * Object containing information about a single host
 */
export type Asset = {
    id: string;
    /**
     * The detected asset type. Supported types: PROGRAMMABLE_LOGIC_CONTROLLER, HUMAN_MACHINE_INTERFACE, INDUSTRIAL_PC, REMOTE_IO, VARIABLE_FREQUENCY_DRIVE, LAPTOP, DESKTOP, SERVER, COMMUNICATIONS_ADAPTER, SENSOR, CAMERA, DOORBELL, PRINTER, SWITCH, ROUTER, FIREWALL, ACCESS_POINT, ACCESS_READER, DOMAIN_CONTROLLER, PHONE, TABLET, TELEVISION, UNKNOWN_ASSET_TYPE.
     */
    type: string;
    /**
     * A user-set override of type, taking one of the same values.
     */
    user_type?: string;
    user_type_modified_by?: string;
    type_determination_method?: AssetTypeDeterminationMethod | null;
    state: AssetState;
    /**
     * The user who last set the state manually. Automatic state changes leave it as is.
     */
    state_modified_by?: string | null;
    duplicate_of_asset_id?: string | null;
    most_recent_ip?: string;
    most_recent_mac?: string;
    hostname?: string;
    serial_number?: string;
    architecture: string;
    firmware?: string;
    os: string;
    os_determination_method?: AssetOsDeterminationMethod | null;
    os_version?: string;
    os_build?: string;
    os_name?: string;
    kernel?: string;
    created_by_log_source: string;
    last_updated_by_log_source: string;
    first_seen_timestamp: Date;
    last_seen_timestamp: Date;
    model_number?: string;
    endpoint_sensor_type?: string;
    endpoint_sensor_version?: string;
    hardware_vendor?: string;
    network_sensors?: Array<string>;
    building_name?: string;
    timestamp: Date;
    related_findings_count?: number;
    tags?: Array<AssetToTag>;
    user_description?: string;
    device_description?: string | null;
    /**
     * The custom attributes set on this asset, sorted by name. An attribute with no value set for this asset is omitted.
     */
    custom_attributes?: Array<AssetCustomAttribute>;
};

/**
 * Represents a communication that is to a known asset, from a known asset, or both
 */
export type AssetCommunication = {
    source_asset_id?: string;
    destination_asset_id?: string;
    sum_bytes: number;
    sum_sessions: number;
    first_seen_timestamp: Date;
    last_seen_timestamp: Date;
};

export type AssetCommunicationGapResult = {
    /**
     * Start time of the communication gap
     */
    timestamp: Date;
    /**
     * Duration of the gap in milliseconds
     */
    length: number;
    /**
     * The asset ID this gap belongs to
     */
    type: string;
};

export type AssetCommunicationHourlyByAsset = {
    /**
     * Number of inbound bytes
     */
    inbound_bytes: number;
    /**
     * Number of outbound bytes
     */
    outbound_bytes: number;
    /**
     * Number of inbound sessions
     */
    inbound_session_count: number;
    /**
     * Number of outbound sessions
     */
    outbound_session_count: number;
    /**
     * Start time of the hourly period
     */
    hour_start: Date;
    /**
     * Well, it's the other asset ID. "EXTERNAL" if not an asset. You know.
     */
    other_asset_id: string;
};

/**
 * Per-asset network-activity summary over the last 90 days.
 */
export type AssetActivity = {
    id: string;
    hostname?: string | null;
    /**
     * The asset type (user-set type if present, else inferred). See Asset.type for the supported values.
     */
    type: string;
    first_seen_timestamp: Date;
    /**
     * Most recent IP for display
     */
    most_recent_ip?: string;
    /**
     * Count of distinct local days the asset communicated in the window
     */
    active_days: number;
    status?: AssetActivityStatus;
    /**
     * The distinct local calendar days (YYYY-MM-DD) the asset communicated. Superseded by active_bucket_list; retained for one release so a frontend deployed before bucketing keeps rendering.
     */
    active_day_list: Array<string>;
    /**
     * The distinct local day blocks (YYYY-MM-DDTHH) the asset communicated, where HH is the block's starting hour — 00/06/12/18 at bucket_hours=6, and 00 only at the default bucket_hours=24.
     */
    active_bucket_list?: Array<string>;
    tags: Array<AssetToTag>;
};

/**
 * One block of a local day the asset communicated in, with a coarse connection count, from the hourly rollup.
 */
export type AssetCommunicationDayByAsset = {
    /**
     * The calendar day (local day encoded at midnight UTC)
     */
    day: Date;
    /**
     * Which block of the day this row covers — the local hour integer-divided by bucket_hours, so bucket_hours=6 yields 0-3 for 00:00/06:00/12:00/18:00. Always 0 at the default bucket_hours=24.
     */
    bucket_index: number;
    /**
     * Coarse connection count in that block (summed rollup session counts)
     */
    connections: number;
};

/**
 * Represents a communication aggregation by hour per connection tuple
 */
export type AssetCommunicationHourlyByTuple = {
    source_asset_id?: string;
    source_ip_address: string;
    /**
     * Hostname of the source asset, absent when the source IP is not a known asset
     */
    source_hostname?: string;
    /**
     * Type of the source asset, preferring the user-set type over the detected one. See Asset.type for the supported values.
     */
    source_asset_type?: string;
    source_port?: number;
    destination_asset_id?: string;
    destination_ip_address: string;
    /**
     * Hostname of the destination asset, absent when the destination IP is not a known asset
     */
    destination_hostname?: string;
    /**
     * Type of the destination asset, preferring the user-set type over the detected one. See Asset.type for the supported values.
     */
    destination_asset_type?: string;
    destination_port?: number;
    protocol: string;
    service?: string;
    sum_bytes: number;
    sum_sessions: number;
    first_seen_timestamp?: Date;
    last_seen_timestamp?: Date;
    timestamp: Date;
};

export type AssetConnection = {
    /**
     * Unique session identifier
     */
    session_id: string;
    /**
     * Reference to asset connection tuple
     */
    connection_tuple_id: string;
    /**
     * Number of bytes transferred
     */
    bytes: number;
    source_port?: number | null;
    /**
     * Service name; Can be comma-delimited listing of multiple
     */
    service?: string;
    /**
     * Connection duration in milliseconds
     */
    duration_ms: number;
    /**
     * Timestamp from the log entry
     */
    log_timestamp: Date;
    /**
     * Record creation timestamp
     */
    timestamp: Date;
    /**
     * Start of the hour for the log timestamp (generated)
     */
    readonly hour_start: Date;
    /**
     * Community identifier as per Zeek logs
     */
    community_id?: string | null;
};

export type AssetProcess = {
    /**
     * Process name or identifier; Full path
     */
    process: string;
    /**
     * UUID of the associated asset
     */
    asset_id: string;
    /**
     * Username associated with the process creation
     */
    username: string;
    /**
     * Timestamp from the original log
     */
    log_timestamp: Date;
    /**
     * Timestamp when the record was inserted into the database
     */
    timestamp: Date;
    /**
     * Unique identifier for the process
     */
    process_guid: string;
    /**
     * GUID of the parent process
     */
    parent_process_guid?: string | null;
    /**
     * Timestamp when the process was seen
     */
    seen_timestamp?: Date | null;
    /**
     * Full command line used to start the process
     */
    command_line?: string | null;
};

/**
 * Represents an grouping of asset endpoint processes, grouped by process.
 */
export type AssetProcessGrouped = {
    asset_id: string;
    process: string;
    username?: string;
    sum_executions: number;
    first_seen_timestamp: Date;
    last_seen_timestamp: Date;
    timestamp: Date;
};

/**
 * Stores a set of query filters, in Queryables format, that are either system-generated or saved by a specific user
 */
export type AssetSavedQuerySet = {
    /**
     * Unique identifier for the saved query set
     */
    id: number;
    /**
     * User-defined name to identify query set
     */
    name: string;
    /**
     * JSON non-array object stored in same format as queryables framework objects
     */
    params: {
        [key: string]: unknown;
    };
    /**
     * Indicates whether this query set was generated by the system
     */
    system_generated?: boolean;
    /**
     * Timestamp when the query set was created
     */
    readonly timestamp?: Date;
    /**
     * Cognito user ID - required if not system-generated
     */
    user_id?: string | null;
};

/**
 * Represents a service that is running on an asset.
 */
export type AssetService = {
    asset_id: string;
    port: number;
    protocol: string;
    service: string;
    first_seen_timestamp: Date;
    last_seen_timestamp: Date;
};

/**
 * Represents a network service that has been observed, grouped by service name with aggregated connection details
 */
export type AssetServiceGrouped = {
    /**
     * The name of the network service
     */
    service: string;
    /**
     * Array of unique destination asset IDs associated with this service
     */
    destination_asset_ids: Array<string>;
    /**
     * Array of unique source asset IDs associated with this service
     */
    source_asset_ids: Array<string>;
    /**
     * Array of unique destination ports used by this service
     */
    destination_ports: Array<number>;
    /**
     * Array of unique network protocols used by this service
     */
    protocols: Array<string>;
    /**
     * The earliest timestamp when this service was observed
     */
    first_seen_timestamp: Date;
    /**
     * The most recent timestamp when this service was observed
     */
    last_seen_timestamp: Date;
};

/**
 * Tags that can be attached to assets
 */
export type AssetTag = {
    id: number;
    description?: string;
    name: string;
    type?: number;
};

/**
 * Tag types for asset tags
 */
export type AssetTagType = {
    id?: number;
    description?: string;
    color?: string;
    name: string;
    parent_type?: string;
};

export type AssetToTag = {
    id?: number;
    asset_id: string;
    tag_id: number;
    created_by?: string | null;
    last_modified_by?: string | null;
    state?: AssetToTagState;
};

/**
 * Junction record associating a tag with a VRF. Fixer auto-applies these tags to assets whose IPs fall in the VRF.
 */
export type VrfToTag = {
    id?: number;
    vrf_id: number;
    tag_id: number;
    created_by?: string | null;
};

/**
 * Junction record associating a tag with a network prefix. Fixer auto-applies these tags to assets whose IPs match the prefix.
 */
export type NetworkPrefixToTag = {
    id?: number;
    network_prefix_id: number;
    tag_id: number;
    created_by?: string | null;
};

export type AssetsByDayRollupElement = {
    day: Date;
    asset_count: number;
};

/**
 * Represents a file stored in a bucket
 */
export type BucketFile = {
    name?: string;
    size?: number;
    last_modified?: Date;
};

export const CommunicationMethod = { email: 'email', slack: 'slack' } as const;

export type CommunicationMethod = typeof CommunicationMethod[keyof typeof CommunicationMethod];

export type DashboardRollup = {
    open_tickets?: number;
    unacknowledged_tickets?: number;
    investigating_tickets?: number;
    current_user_open_tickets?: number;
    active_assets?: number;
    active_assets_last_period?: number;
    assets_last_period?: number;
    new_assets?: number;
    new_assets_last_period?: number;
    assets_in_open_findings?: number;
    ticket_counts?: Array<{
        start?: Date;
        end?: Date;
        severity_occurrences?: Array<string>;
    }>;
};

export type DataTransferByDayRollupElement = {
    day: Date;
    bytes: number;
};

export type DeleteResponse = {
    success?: boolean;
};

/**
 * Object which contains metadata for a specific finding
 */
export type Finding = {
    /**
     * A UUID uniquely identifying an event that may be comprised of 1 or more component findings
     */
    id?: string;
    collision_slug?: string;
    ticket_id?: string;
    ads_version?: string;
    detection_version?: string;
    detection_name?: string;
    mermaid_diagram?: string;
    severity?: string;
    first_event_timestamp?: Date;
    last_event_timestamp?: Date;
    last_analysis_timestamp?: Date;
    time_since?: string;
    assignee?: string;
    status?: string;
    related_assets_count?: number;
};

export type FindingHistory = {
    id?: string;
    finding_id?: string;
    first_event_timestamp?: Date;
    last_event_timestamp?: Date;
    timestamp?: Date;
    type?: string;
    output_path?: string;
    raw_events?: string;
    related_assets?: Array<string>;
};

export type FindingHistoryLimited = {
    id?: string;
    finding_id?: string;
    first_event_timestamp?: Date;
    last_event_timestamp?: Date;
    timestamp?: Date;
    type?: string;
    raw_events?: Array<{
        [key: string]: unknown;
    }>;
};

export type FullFinding = {
    finding?: Finding;
    gemini_history?: Array<GeminiHistory>;
    history?: Array<FindingHistory>;
    ticket?: Ticket;
    ticket_history?: Array<TicketHistory>;
    comments?: Array<TicketComment>;
    related_assets?: Array<string>;
};

export type FreeformObject = {
    [key: string]: unknown;
};

/**
 * Represents an event/response from Galvanick's Gemini chatbot
 */
export type GeminiHistory = {
    id?: string;
    finding_id?: string;
    detail?: string;
    state?: string;
    responding_user?: string;
    timestamp?: Date;
};

/**
 * Defines settings used for Gemini integration
 */
export type GeminiSettings = {
    slack_oauth_client_id?: string;
    slack_oauth_redirect_uri?: string;
    slack_install_key?: string;
    slack_installed?: boolean;
    slack_enabled?: boolean;
    email_enabled?: boolean;
    email_summary_enabled?: boolean;
    emails?: Array<string>;
    escalations_enabled?: boolean;
    escalation_rollup?: boolean;
    escalation_map?: {
        [key: string]: Array<string>;
    };
    notification_map?: {
        informational?: Array<CommunicationMethod>;
        low?: Array<CommunicationMethod>;
        medium?: Array<CommunicationMethod>;
        high?: Array<CommunicationMethod>;
        critical?: Array<CommunicationMethod>;
    };
    sla_map?: {
        [key: string]: number;
    };
};

/**
 * List of hunt reports
 */
export type HuntReportList = {
    files?: Array<BucketFile>;
};

/**
 * An IP address as recorded for an asset
 */
export type IpAddress = {
    id: string;
    asset_id: string;
    ip_address: string;
    created_by_log_source: string;
    last_updated_by_log_source: string;
    first_seen_timestamp: Date;
    last_seen_timestamp: Date;
    expiry_timestamp?: Date;
    /**
     * The confirmed 802.1Q VLAN ID for this IP sighting. 0 or null means no VLAN was confirmed.
     */
    vlan_id?: number | null;
    timestamp: Date;
};

/**
 * A Suricata (IDS) event row from the suricata_intake table
 */
export type SuricataEvent = {
    id: string;
    log_source: string;
    /**
     * The full raw Suricata event as a JSON object
     */
    raw_log: {
        [key: string]: unknown;
    };
    sensor_type: string;
    sensor_name: string;
    app_name?: string | null;
    category: string;
    signature: string;
    signature_id: number;
    protocol: string;
    src_ip: string;
    dst_ip: string;
    src_port: number;
    dst_port: number;
    severity: number;
    /**
     * Suricata flow id, returned as a string to avoid JS precision loss on large bigints
     */
    flow_id: string;
    load_start_time: Date;
    upload_time: Date;
    timestamp: Date;
    log_timestamp: Date;
    vlan_id?: number | null;
};

/**
 * Alert activity for one Suricata rule category, aggregated from suricata_intake
 */
export type SuricataCategoryGroup = {
    /**
     * Null for alerts the rule left uncategorised; they group together
     */
    category?: string | null;
    /**
     * The most severe level any rule in the category fired at. Suricata's scale is descending, so 1 is the highest priority.
     */
    severity: number;
    /**
     * Distinct rules that fired in this category
     */
    rules: number;
    alerts: number;
    /**
     * Distinct source hosts
     */
    sources: number;
    /**
     * Distinct destination hosts
     */
    destinations: number;
    first_seen: Date;
    last_seen: Date;
};

/**
 * Alert activity for one Suricata (IDS) rule, aggregated from suricata_intake
 */
export type SuricataRuleGroup = {
    /**
     * The rule's stable identity, and the field the group is keyed on
     */
    signature_id: number;
    /**
     * One of the rule's wordings (the last alphabetically). Authors reword a signature without changing its ID, so the group may cover several.
     */
    signature: string;
    category?: string | null;
    /**
     * The most severe level the rule fired at. Suricata's scale is descending, so 1 is the highest priority.
     */
    severity: number;
    alerts: number;
    /**
     * Distinct source hosts the rule fired on
     */
    sources: number;
    /**
     * Distinct destination hosts the rule fired on
     */
    destinations: number;
    first_seen: Date;
    last_seen: Date;
};

/**
 * A Mac address as recorded for an asset
 */
export type MacAddress = {
    id: string;
    asset_id: string;
    mac_address: string;
    created_by_log_source: string;
    last_updated_by_log_source: string;
    first_seen_timestamp: Date;
    last_seen_timestamp: Date;
    timestamp: Date;
    /**
     * IEEE-registered vendor for the MAC's OUI. Null if the OUI has no known vendor.
     */
    vendor?: string | null;
};

export type ProcessTree = {
    /**
     * The unique identifier for the process.
     */
    process_guid: string;
    /**
     * The unique identifier for the parent process. Null if root.
     */
    parent_process_guid?: string | null;
    /**
     * Name of the process.
     */
    process: string;
    /**
     * The associated asset ID.
     */
    asset_id: string;
    /**
     * Username of the person who initiated the process.
     */
    username: string;
    /**
     * The timestamp when the process was logged.
     */
    log_timestamp: Date;
    /**
     * The relationship of the process to the queried process.
     */
    relation_type: 'self' | 'ancestor' | 'descendant';
    /**
     * Whether ancestors have sibling processes; Will always be false for descendants.
     */
    has_siblings: boolean;
    /**
     * Depth in the hierarchy (0 = self, negative = ancestors, positive = descendants).
     */
    depth: number;
    /**
     * Command line call used to start process.
     */
    command_line?: string;
    /**
     * Will be true if username that executed process differs from that of parent process.
     */
    username_changed: boolean;
};

export type ReportEntry = {
    name?: string;
    path?: string;
};

export type ReportList = Array<ReportEntry>;

/**
 * A ticket
 */
export type Ticket = {
    id: string;
    timestamp?: Date;
};

/**
 * Object containing note data which is attached to a finding
 */
export type TicketComment = {
    id: string;
    user_id: string;
    ticket_id: string;
    description: string;
    timestamp?: Date;
};

/**
 * The client-supplied half of a ticket comment. `id` is assigned by the database and `user_id` is taken from the caller's token, so neither is accepted here.
 *
 */
export type TicketCommentInput = {
    ticket_id: string;
    description: string;
};

export type TicketHistory = {
    id: string;
    ticket_id: string;
    modified_by: string;
    status: string;
    closure_code?: string;
    closure_note?: string;
    closure_reason?: string;
    tuning_request?: string;
    assignee?: string;
    timestamp?: Date;
};

export type TicketStatusChange = {
    status: string;
    assignee?: string;
    assignee_changed: boolean;
    start_time_ms: number;
    end_time_ms?: number;
    id: string;
    severity: string;
    detection_name: string;
    color: string;
    modified_by: string;
    ticket_history_id: string;
    ticket_id: string;
    closure_code?: string;
    closure_note?: string;
    closure_reason?: string;
};

export type User = {
    id: string;
    given_name: string;
    family_name: string;
    email: string;
    /**
     * False for a disabled account, which cannot sign in. Carried on the list as well as on UserDetail, so account state can be shown without reading each user.
     *
     * Disabling is how access is taken away — there is no delete — so a false here is the normal state for a departed user rather than something anomalous. Set through PUT /users/{id}/enabled.
     *
     */
    enabled: boolean;
};

/**
 * The tenant a user belongs to.
 */
export type Organization = {
    id?: string;
    name?: string;
    /**
     * The organization's immutable identifier, defaulted to the name when the organization was created. The name can be changed afterwards and this cannot, so key any record of an organization on this rather than on the name.
     *
     */
    alias?: string;
};

/**
 * An organization, with how its users sign in and whether the caller may change them.
 *
 */
export type OrganizationSummary = Organization & {
    /**
     * Whether the caller may change users in this organization. False for organizations other than their own, which are read-only to them. Agrees with the manageable flag on that organization's users, and is the only signal available for an organization with no members.
     *
     */
    manageable?: boolean;
    /**
     * Whether this organization brokers logins to an identity provider. This is the organization's posture, not a claim about every member: an organization reporting idp can still contain members added to it directly who sign in with a password. Read each user's own auth_method for that.
     *
     */
    auth_method?: AuthMethod;
    /**
     * The providers linked to this organization, empty when auth_method is password. May hold a disabled provider, so read auth_method rather than testing this for emptiness.
     *
     */
    identity_providers?: Array<IdentityProvider>;
};

/**
 * An organization together with its users.
 */
export type OrganizationDetail = OrganizationSummary & {
    users?: Array<UserDetail>;
};

/**
 * A group that grants a portal role, and so decides the portal access of everyone in it.
 *
 */
export type PortalGroup = {
    id?: string;
    name?: string;
    /**
     * The portal role this group grants.
     */
    role?: 'galvanick:admin' | 'galvanick:user' | 'galvanick:read-only';
    /**
     * Whether the group operations will move a user in or out of this group. A group the portal does not manage still grants its role, and so is reported on a user, but membership in it is managed in the identity provider rather than through this API.
     *
     */
    assignable?: boolean;
};

/**
 * How authentication happens. `idp` means logins are brokered to an external identity provider, which owns the credential — there is no password here to set, and the password reset does not apply. `password` means the credential is held here.
 *
 * Read this per resource, because on an organization and on a user it answers different questions. On an organization it reports whether that organization brokers logins at all. On a user it reports how *that user* signs in, which is decided by their membership: an organization that brokers logins can still hold members added to it directly, who keep a password here.
 *
 * So an organization with `idp` may contain a user with `password`, and that is not a contradiction. Never infer a user's method from their organization's — use the one on the user, which is what the reset endpoint enforces against.
 *
 */
export const AuthMethod = { idp: 'idp', password: 'password' } as const;

/**
 * How authentication happens. `idp` means logins are brokered to an external identity provider, which owns the credential — there is no password here to set, and the password reset does not apply. `password` means the credential is held here.
 *
 * Read this per resource, because on an organization and on a user it answers different questions. On an organization it reports whether that organization brokers logins at all. On a user it reports how *that user* signs in, which is decided by their membership: an organization that brokers logins can still hold members added to it directly, who keep a password here.
 *
 * So an organization with `idp` may contain a user with `password`, and that is not a contradiction. Never infer a user's method from their organization's — use the one on the user, which is what the reset endpoint enforces against.
 *
 */
export type AuthMethod = typeof AuthMethod[keyof typeof AuthMethod];

/**
 * An external login source linked to an organization, named so a client can label the sign-in a user will be sent to.
 *
 */
export type IdentityProvider = {
    /**
     * The provider's internal identifier in Keycloak.
     */
    alias: string;
    /**
     * The provider's display name, suitable for showing to a user. Empty when the provider has none configured; do not fall back to the alias, which is an internal identifier.
     *
     */
    name: string;
    /**
     * False for a provider that is linked but currently brokers no logins. Such a provider does not make its organization's auth_method idp, which is why the method is reported separately rather than inferred from this list.
     *
     */
    enabled: boolean;
};

/**
 * The request to create a user. Every field is required: a create decides both where a user belongs and what they may do, and neither has a safe default.
 *
 */
export type NewUser = {
    /**
     * Also becomes the sign-in name. The realm registers email as username and moves the username when the email changes, so there is no separate name to choose.
     *
     */
    email: string;
    given_name: string;
    family_name: string;
    /**
     * The organization to create the user in. Must be one the caller manages, per manageable on the organizations list; any other is refused with 403.
     *
     */
    organization_id: string;
    /**
     * The portal group deciding the user's role, as returned by GET /users/groups. Only an assignable group is accepted. The created user is left in this group and in no other role-granting group.
     *
     */
    group_id: string;
};

/**
 * A change to a user's profile. Every field is optional and an omitted one is left as it is, so send only what is changing; a body naming no field succeeds without changing anything. A field present but empty is refused with 400 rather than clearing the value.
 *
 * Group membership and account state are not here. Roles are changed through /users/{id}/groups/{group_id}.
 *
 */
export type UserUpdate = {
    /**
     * Also the sign-in name, so changing this changes how the user signs in. Stored lowercased; the response reports the stored form. Refused with 409 for a user whose auth_method is idp, and for an address already in use.
     *
     */
    email?: string;
    given_name?: string;
    family_name?: string;
};

/**
 * The state to put a user's portal access into. Required rather than defaulted: a body omitting it is refused with 400, so a malformed request can never read as a disable.
 *
 * Separate from UserUpdate because this is the one change that takes access away, and because it carries a side effect the profile fields do not — a disable removes the user's sessions.
 *
 */
export type UserEnabledInput = {
    /**
     * True restores access, false takes it away. See the operation description for why a disable is not immediate.
     *
     */
    enabled: boolean;
};

/**
 * A newly created user together with the one-time password to relay. The user is reported exactly as GET /users/{id} would report them, so there is no second shape to handle.
 *
 */
export type CreatedUser = UserDetail & {
    /**
     * Returned only here. An administrator who loses it issues a new one through the password reset. Treat as a secret in transit and at rest.
     *
     */
    temporary_password: string;
};

/**
 * A generated password an administrator relays to a user, who must replace it at their next sign-in. Returned only by the reset that created it; Keycloak stores it hashed, so it cannot be read back.
 *
 */
export type TemporaryPassword = {
    /**
     * At least 14 characters, generated with at least 112 bits of entropy. Treat as a secret in transit and at rest.
     *
     */
    temporary_password: string;
};

/**
 * The identity provider's own name for an event. This is the full set these endpoints report and the set the type query parameter accepts; the realm records more, down to each token exchange, and the rest is noise for an account history.
 *
 * `LOGIN` is a successful sign-in, including one brokered to an identity provider: the broker records its own event, and the session that results still lands here. A refreshed token is not a sign-in and is not recorded, so these count actual sign-ins. `LOGIN_ERROR` is an attempt that failed, with the reason in error. `IDENTITY_PROVIDER_LOGIN` is a sign-in brokered to a provider, `IDENTITY_PROVIDER_LOGIN_ERROR` one that failed, and `IDENTITY_PROVIDER_FIRST_LOGIN` the first success for the account, which is where a provider identity gets linked to it.
 *
 * `UPDATE_CREDENTIAL` is the user replacing a credential, e.g. setting their own password after being issued a temporary one; `RESET_PASSWORD` is a password replaced through the reset a user requests for themselves.
 *
 */
export const UserEventType = {
    login: 'LOGIN',
    login_error: 'LOGIN_ERROR',
    identity_provider_login: 'IDENTITY_PROVIDER_LOGIN',
    identity_provider_login_error: 'IDENTITY_PROVIDER_LOGIN_ERROR',
    identity_provider_first_login: 'IDENTITY_PROVIDER_FIRST_LOGIN',
    update_credential: 'UPDATE_CREDENTIAL',
    reset_password: 'RESET_PASSWORD'
} as const;

/**
 * The identity provider's own name for an event. This is the full set these endpoints report and the set the type query parameter accepts; the realm records more, down to each token exchange, and the rest is noise for an account history.
 *
 * `LOGIN` is a successful sign-in, including one brokered to an identity provider: the broker records its own event, and the session that results still lands here. A refreshed token is not a sign-in and is not recorded, so these count actual sign-ins. `LOGIN_ERROR` is an attempt that failed, with the reason in error. `IDENTITY_PROVIDER_LOGIN` is a sign-in brokered to a provider, `IDENTITY_PROVIDER_LOGIN_ERROR` one that failed, and `IDENTITY_PROVIDER_FIRST_LOGIN` the first success for the account, which is where a provider identity gets linked to it.
 *
 * `UPDATE_CREDENTIAL` is the user replacing a credential, e.g. setting their own password after being issued a temporary one; `RESET_PASSWORD` is a password replaced through the reset a user requests for themselves.
 *
 */
export type UserEventType = typeof UserEventType[keyof typeof UserEventType];

/**
 * One thing that happened to a user's account, as the identity provider recorded it. Only the types below are reported: the realm records more, down to each token exchange, and the rest is noise for an account history.
 *
 * Only what the user did themselves is here. A change an administrator makes through this API — issuing a temporary password, editing a name or email, moving a user between groups, disabling an account — is recorded in a separate admin event store this does not read. The temporary password an administrator issues is therefore absent, while the password the user then sets at the login form appears as UPDATE_CREDENTIAL.
 *
 */
export type UserEvent = {
    /**
     * Whose account it happened to. Carried on a single user's events too, so both reads report the same shape.
     *
     */
    user_id: string;
    type: UserEventType;
    /**
     * When it happened, UTC.
     */
    at: Date;
    /**
     * The application it happened through, e.g. portal. Absent for an event with no application behind it.
     *
     */
    client_id?: string;
    /**
     * Why a failure failed, as the identity provider names it. Absent on a success, so its presence is what separates a failed sign-in from one that worked.
     *
     * `invalid_user_credentials` is a wrong password. Failures the provider could not attribute to a user account are not reported at all.
     *
     */
    error?: string;
};

/**
 * A user with the account state and portal access an administrator needs, beyond the identity fields on User.
 *
 */
export type UserDetail = User & {
    /**
     * Null when no creation time is recorded for the account.
     */
    created_at?: Date | null;
    email_verified?: boolean;
    /**
     * How this user signs in, decided by how they belong to their organization rather than by the organization alone. A member the organization's identity provider owns reports idp; one added to the organization directly reports password, even when that organization brokers logins.
     *
     * This is the value the reset endpoint enforces against: it refuses an idp user with 409. Gate the reset on this rather than on the organization's auth_method, which would wrongly hide the action from a password member of a brokered organization.
     *
     */
    auth_method?: AuthMethod;
    /**
     * Whether the user has a password they must replace before they reach the application — the state a password reset leaves them in. Use it to show that a temporary password is still outstanding, and that reissuing one would invalidate the password already handed out.
     * Clears itself once the user chooses a new password.
     *
     */
    must_change_password?: boolean;
    /**
     * Every role the user's groups grant, including roles implied by another — an administrator also holds the user role. This is current state, so it can differ from the roles in an active session that predates a recent group change, until that session renews. Empty if the user holds no portal role.
     *
     */
    roles?: Array<'galvanick:admin' | 'galvanick:user' | 'galvanick:read-only'>;
    /**
     * Every role-granting group the user belongs to. Membership is not constrained to one group, so this may hold more than one, or none at all. A group the portal does not manage appears here too, because it still decides the user's roles. Check the assignable field before offering to remove one.
     *
     */
    groups?: Array<PortalGroup>;
    /**
     * The organization the user belongs to, or null when they belong to none. Carries no manageable flag: use the one on the user itself.
     *
     */
    organization?: Organization | null;
    /**
     * Whether the group operations will act on this user, for the caller making this request. False for users outside the caller's own organization, which are read-only to them. Because it depends on who is asking, the same user can be manageable for one caller and not another. Prefer this over comparing organizations yourself, so what the UI offers matches what the API enforces.
     *
     */
    manageable?: boolean;
};

export type EndpointLogSourceWritable = {
    /**
     * Key of the channel, e.g. SECURITY.
     */
    log_source: string;
    /**
     * The Windows event log channel, e.g. Microsoft-Windows-Sysmon/Operational.
     */
    log_name: string;
    label: string;
    /**
     * An admin's override of hours without an event before Data missing. 0 never flags this channel; null uses default_missing_after_hours.
     */
    missing_after_hours: number | null;
};

/**
 * An endpoint sensor we installed, registered automatically by fixer on the sensor's first upload, joined to the asset it runs on. Only network_sensor_id and state are writable: the rest are fixer's or the asset's, and the Description is the asset's user_description, edited via PATCH /assets/{id} rather than duplicated here.
 */
export type EndpointSensorWritable = {
    id: number;
    /**
     * Appliance this sensor is supposed to upload through. Intent, deliberately not reported_via -- a gap between the two is a misconfigured sensor. Defaulted to the observer that delivered the first upload.
     */
    network_sensor_id?: number | null;
    /**
     * INACTIVE means the install was deliberately retired and must not alarm.
     */
    state: 'ACTIVE' | 'INACTIVE';
    last_modified_by?: string | null;
};

export type AssetConnectionWritable = {
    /**
     * Unique session identifier
     */
    session_id: string;
    /**
     * Reference to asset connection tuple
     */
    connection_tuple_id: string;
    /**
     * Number of bytes transferred
     */
    bytes: number;
    source_port?: number | null;
    /**
     * Service name; Can be comma-delimited listing of multiple
     */
    service?: string;
    /**
     * Connection duration in milliseconds
     */
    duration_ms: number;
    /**
     * Timestamp from the log entry
     */
    log_timestamp: Date;
    /**
     * Record creation timestamp
     */
    timestamp: Date;
    /**
     * Community identifier as per Zeek logs
     */
    community_id?: string | null;
};

/**
 * Stores a set of query filters, in Queryables format, that are either system-generated or saved by a specific user
 */
export type AssetSavedQuerySetWritable = {
    /**
     * Unique identifier for the saved query set
     */
    id: number;
    /**
     * User-defined name to identify query set
     */
    name: string;
    /**
     * JSON non-array object stored in same format as queryables framework objects
     */
    params: {
        [key: string]: unknown;
    };
    /**
     * Indicates whether this query set was generated by the system
     */
    system_generated?: boolean;
    /**
     * Cognito user ID - required if not system-generated
     */
    user_id?: string | null;
};

/**
 * Maximum number of items to return.
 */
export type LimitParam = number;

/**
 * Event types to return, repeated for more than one — `?type=LOGIN&type=LOGIN_ERROR`. Empty returns every type these endpoints report, which GET /users/events/types lists. A type outside that set is refused with 400.
 *
 */
export type EventTypeParam = Array<UserEventType>;

/**
 * Only return events at or after this instant, RFC 3339. Open ended when omitted, and held to the 30-day window the identity provider retains: an earlier value returns what is there rather than failing.
 *
 */
export type DateFromParam = Date;

/**
 * Only return events at or before this instant, RFC 3339. Open ended when omitted, so the newest events are returned.
 *
 */
export type DateToParam = Date;

/**
 * Maximum number of events to return. Allowed higher than the limit on the other lists: an event is a fraction of the size of the records those page, and reading them costs one request to the identity provider however many are asked for.
 *
 */
export type EventLimitParam = number;

/**
 * Page number
 */
export type OffsetParam = number;

/**
 * Field name to use for ordering list results
 */
export type OrderBy = string;

/**
 * If present and true, list results will be ordered descending
 */
export type OrderIsDesc = boolean;

/**
 * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
 *
 */
export type QueryablesParam = Queryables;

/**
 * The resource's unique identifier
 */
export type IdParam = string;

/**
 * UUID of the asset
 */
export type AssetIdParam = string;

/**
 * ID of the custom asset attribute definition
 */
export type CustomAssetAttributeDefinitionIdParam = number;

/**
 * ID of a portal group, as returned by GET /users/groups. Any other value is rejected with 400.
 *
 */
export type GroupIdParam = string;

export type AafhDeleteData = {
    body: Aafh;
    path?: never;
    query?: never;
    url: '/aafh';
};

export type AafhDeleteErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AafhDeleteResponses = {
    /**
     * Successful operation
     */
    200: DeleteResponse;
};

export type AafhDeleteResponse = AafhDeleteResponses[keyof AafhDeleteResponses];

export type AafhGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/aafh';
};

export type AafhGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AafhGetResponses = {
    /**
     * Successful operation
     */
    200: Array<Aafh>;
};

export type AafhGetResponse = AafhGetResponses[keyof AafhGetResponses];

export type AafhPostData = {
    body: Aafh;
    path?: never;
    query?: never;
    url: '/aafh';
};

export type AafhPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AafhPostResponses = {
    /**
     * Asset linked to the finding
     */
    201: Aafh;
};

export type AafhPostResponse = AafhPostResponses[keyof AafhPostResponses];

export type AdsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/ads';
};

export type AdsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AdsGetResponses = {
    /**
     * Successful operation
     */
    200: Array<Ads>;
};

export type AdsGetResponse = AdsGetResponses[keyof AdsGetResponses];

export type AdsIdGetData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/ads/{id}';
};

export type AdsIdGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AdsIdGetResponses = {
    /**
     * Successful operation
     */
    200: Ads;
};

export type AdsIdGetResponse = AdsIdGetResponses[keyof AdsIdGetResponses];

export type AssetsDistinctValuesGetData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Column to enumerate. One of an allowlisted set.
         */
        field: AssetDistinctValuesField;
    };
    url: '/assets/distinct-values';
};

export type AssetsDistinctValuesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsDistinctValuesGetResponses = {
    /**
     * Successful operation
     */
    200: {
        values?: Array<{
            value?: string;
            count?: number;
        }>;
    };
};

export type AssetsDistinctValuesGetResponse = AssetsDistinctValuesGetResponses[keyof AssetsDistinctValuesGetResponses];

export type AssetsIdsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/assets/ids';
};

export type AssetsIdsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsIdsGetResponses = {
    /**
     * Successful operation
     */
    200: Array<string>;
};

export type AssetsIdsGetResponse = AssetsIdsGetResponses[keyof AssetsIdsGetResponses];

export type AssetsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/assets';
};

export type AssetsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<Asset>;
    };
};

export type AssetsGetResponse = AssetsGetResponses[keyof AssetsGetResponses];

export type AssetsFindingsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
        findings_history?: 'true' | 'false';
        'cf.open'?: 'true' | 'false';
        'cf.closed'?: 'true' | 'false';
    };
    url: '/assets/findings';
};

export type AssetsFindingsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsFindingsGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<Asset>;
    };
};

export type AssetsFindingsGetResponse = AssetsFindingsGetResponses[keyof AssetsFindingsGetResponses];

export type AssetsIdGetData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/assets/{id}';
};

export type AssetsIdGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsIdGetResponses = {
    /**
     * Successful operation
     */
    200: Asset;
};

export type AssetsIdGetResponse = AssetsIdGetResponses[keyof AssetsIdGetResponses];

export type AssetsIdPatchData = {
    body?: PatchRequest;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/assets/{id}';
};

export type AssetsIdPatchErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsIdPatchResponses = {
    /**
     * Successful operation
     */
    200: Asset;
};

export type AssetsIdPatchResponse = AssetsIdPatchResponses[keyof AssetsIdPatchResponses];

export type AssetsAssetIdStatePatchData = {
    body: AssetStateInput;
    path: {
        /**
         * UUID of the asset
         */
        asset_id: string;
    };
    query?: never;
    url: '/assets/{asset_id}/state';
};

export type AssetsAssetIdStatePatchErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsAssetIdStatePatchResponses = {
    /**
     * Successful operation
     */
    200: Asset;
};

export type AssetsAssetIdStatePatchResponse = AssetsAssetIdStatePatchResponses[keyof AssetsAssetIdStatePatchResponses];

export type AssetsAssetIdIpAddressesGetData = {
    body?: never;
    path: {
        /**
         * UUID of the asset
         */
        asset_id: string;
    };
    query?: never;
    url: '/assets/{asset_id}/ip_addresses';
};

export type AssetsAssetIdIpAddressesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsAssetIdIpAddressesGetResponses = {
    /**
     * Successful operation
     */
    200: Array<IpAddress>;
};

export type AssetsAssetIdIpAddressesGetResponse = AssetsAssetIdIpAddressesGetResponses[keyof AssetsAssetIdIpAddressesGetResponses];

export type AssetsAssetIdMacAddressesGetData = {
    body?: never;
    path: {
        /**
         * UUID of the asset
         */
        asset_id: string;
    };
    query?: never;
    url: '/assets/{asset_id}/mac_addresses';
};

export type AssetsAssetIdMacAddressesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsAssetIdMacAddressesGetResponses = {
    /**
     * Successful operation
     */
    200: Array<MacAddress>;
};

export type AssetsAssetIdMacAddressesGetResponse = AssetsAssetIdMacAddressesGetResponses[keyof AssetsAssetIdMacAddressesGetResponses];

export type AssetsAssetIdCustomAttributesDefinitionIdPutData = {
    body: AssetCustomAttributeValueInput;
    path: {
        /**
         * UUID of the asset
         */
        asset_id: string;
        /**
         * ID of the custom asset attribute definition
         */
        definition_id: number;
    };
    query?: never;
    url: '/assets/{asset_id}/custom_attributes/{definition_id}';
};

export type AssetsAssetIdCustomAttributesDefinitionIdPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsAssetIdCustomAttributesDefinitionIdPutResponses = {
    /**
     * Successful operation
     */
    200: AssetCustomAttributeValues;
};

export type AssetsAssetIdCustomAttributesDefinitionIdPutResponse = AssetsAssetIdCustomAttributesDefinitionIdPutResponses[keyof AssetsAssetIdCustomAttributesDefinitionIdPutResponses];

export type AssetsCommunicationsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/assets/communications';
};

export type AssetsCommunicationsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsCommunicationsGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<AssetCommunication>;
    };
};

export type AssetsCommunicationsGetResponse = AssetsCommunicationsGetResponses[keyof AssetsCommunicationsGetResponses];

export type AssetsCommunicationsGapsGetData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Start of the time range (RFC3339 format)
         */
        start_time: Date;
        /**
         * End of the time range (RFC3339 format)
         */
        end_time: Date;
        /**
         * List of asset IDs to analyze (repeat parameter for multiple values)
         */
        asset_ids: Array<string>;
        /**
         * Minimum length of gap to include, in milliseconds
         */
        min_length_ms: number;
    };
    url: '/assets/communications/gaps';
};

export type AssetsCommunicationsGapsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsCommunicationsGapsGetResponses = {
    /**
     * Successful response with communication gaps
     */
    200: Array<AssetCommunicationGapResult>;
};

export type AssetsCommunicationsGapsGetResponse = AssetsCommunicationsGapsGetResponses[keyof AssetsCommunicationsGapsGetResponses];

export type AssetsCommunicationsHourlyByAssetGetData = {
    body?: never;
    path?: never;
    query: {
        asset_id: string;
    };
    url: '/assets/communications/hourly/by-asset';
};

export type AssetsCommunicationsHourlyByAssetGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsCommunicationsHourlyByAssetGetResponses = {
    /**
     * Successful operation
     */
    200: Array<AssetCommunicationHourlyByAsset>;
};

export type AssetsCommunicationsHourlyByAssetGetResponse = AssetsCommunicationsHourlyByAssetGetResponses[keyof AssetsCommunicationsHourlyByAssetGetResponses];

export type AssetsCommunicationsDaysByAssetGetData = {
    body?: never;
    path?: never;
    query: {
        /**
         * The asset to aggregate communications for
         */
        asset_id: string;
        /**
         * Start of the time range (RFC3339 format)
         */
        start_time: Date;
        /**
         * End of the time range (RFC3339 format)
         */
        end_time: Date;
        /**
         * IANA timezone name used to bucket hours into calendar days
         */
        timezone: string;
        /**
         * Split each local day into 24/bucket_hours equal blocks and return one row per block, so the caller can see when in the day the asset was quiet. Defaults to 24 (one row per day, matching the pre-bucketing behaviour).
         */
        bucket_hours?: 1 | 6 | 24;
    };
    url: '/assets/communications/days/by-asset';
};

export type AssetsCommunicationsDaysByAssetGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsCommunicationsDaysByAssetGetResponses = {
    /**
     * Successful operation
     */
    200: Array<AssetCommunicationDayByAsset>;
};

export type AssetsCommunicationsDaysByAssetGetResponse = AssetsCommunicationsDaysByAssetGetResponses[keyof AssetsCommunicationsDaysByAssetGetResponses];

export type AssetsNetworkActivityPostData = {
    body?: {
        /**
         * Restrict results to these asset IDs. The list view resolves them from the filter toolbar via the assets ID-only endpoint and passes them here, so this endpoint inherits the full asset filter surface.
         */
        asset_ids?: Array<string>;
    };
    path?: never;
    query: {
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
        /**
         * IANA timezone name used to bucket connections into calendar days
         */
        timezone: string;
        /**
         * Granularity of active_bucket_list — each local day is split into 24/bucket_hours blocks. Defaults to 24, one block per day. Hourly is allowed but heavy: one 13-byte key per active block means a fully-busy asset contributes ~30KB, so a page of 100 approaches 3MB. The default page is 20 and callers only ask for hourly on request.
         */
        bucket_hours?: 1 | 6 | 24;
        /**
         * Restrict the page to assets in one status. Applied by the count query too, so the paginator total matches the filtered rows. Omit for all.
         */
        status?: AssetActivityStatus;
    };
    url: '/assets/network-activity';
};

export type AssetsNetworkActivityPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsNetworkActivityPostResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<AssetActivity>;
    };
};

export type AssetsNetworkActivityPostResponse = AssetsNetworkActivityPostResponses[keyof AssetsNetworkActivityPostResponses];

export type AssetsCommunicationsHourlyByTupleGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/assets/communications/hourly/by-tuple';
};

export type AssetsCommunicationsHourlyByTupleGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsCommunicationsHourlyByTupleGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<AssetCommunicationHourlyByTuple>;
    };
};

export type AssetsCommunicationsHourlyByTupleGetResponse = AssetsCommunicationsHourlyByTupleGetResponses[keyof AssetsCommunicationsHourlyByTupleGetResponses];

export type AssetsConnectionsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/assets/connections';
};

export type AssetsConnectionsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsConnectionsGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<AssetConnection>;
    };
};

export type AssetsConnectionsGetResponse = AssetsConnectionsGetResponses[keyof AssetsConnectionsGetResponses];

export type AssetsProcessTreeGetData = {
    body?: never;
    path?: never;
    query: {
        guid: string;
    };
    url: '/assets/process-tree';
};

export type AssetsProcessTreeGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsProcessTreeGetResponses = {
    /**
     * Successful operation
     */
    200: Array<ProcessTree>;
};

export type AssetsProcessTreeGetResponse = AssetsProcessTreeGetResponses[keyof AssetsProcessTreeGetResponses];

export type AssetsProcessesGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/assets/processes';
};

export type AssetsProcessesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsProcessesGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<AssetProcess>;
    };
};

export type AssetsProcessesGetResponse = AssetsProcessesGetResponses[keyof AssetsProcessesGetResponses];

export type AssetsAssetIdProcessesGetData = {
    body?: never;
    path: {
        /**
         * UUID of the asset
         */
        asset_id: string;
    };
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/assets/{asset_id}/processes';
};

export type AssetsAssetIdProcessesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsAssetIdProcessesGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<AssetProcessGrouped>;
    };
};

export type AssetsAssetIdProcessesGetResponse = AssetsAssetIdProcessesGetResponses[keyof AssetsAssetIdProcessesGetResponses];

export type AssetsAssetIdProcessesProcessGetData = {
    body?: never;
    path: {
        /**
         * UUID of the asset
         */
        asset_id: string;
        /**
         * The path of the process
         */
        process: string;
    };
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/assets/{asset_id}/processes/{process}';
};

export type AssetsAssetIdProcessesProcessGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsAssetIdProcessesProcessGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<AssetProcess>;
    };
};

export type AssetsAssetIdProcessesProcessGetResponse = AssetsAssetIdProcessesProcessGetResponses[keyof AssetsAssetIdProcessesProcessGetResponses];

export type AssetsQuerySetsDeleteData = {
    body?: {
        id: number;
    };
    path?: never;
    query?: never;
    url: '/assets/query-sets';
};

export type AssetsQuerySetsDeleteErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsQuerySetsDeleteResponses = {
    /**
     * Successful operation
     */
    200: DeleteResponse;
};

export type AssetsQuerySetsDeleteResponse = AssetsQuerySetsDeleteResponses[keyof AssetsQuerySetsDeleteResponses];

export type AssetsQuerySetsGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/assets/query-sets';
};

export type AssetsQuerySetsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsQuerySetsGetResponses = {
    /**
     * Successful operation
     */
    200: Array<AssetSavedQuerySet>;
};

export type AssetsQuerySetsGetResponse = AssetsQuerySetsGetResponses[keyof AssetsQuerySetsGetResponses];

export type AssetsQuerySetsPostData = {
    body?: AssetSavedQuerySetWritable;
    path?: never;
    query?: never;
    url: '/assets/query-sets';
};

export type AssetsQuerySetsPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsQuerySetsPostResponses = {
    /**
     * Successful operation
     */
    200: AssetSavedQuerySet;
};

export type AssetsQuerySetsPostResponse = AssetsQuerySetsPostResponses[keyof AssetsQuerySetsPostResponses];

export type AssetsQuerySetsPutData = {
    body?: AssetSavedQuerySetWritable;
    path?: never;
    query?: never;
    url: '/assets/query-sets';
};

export type AssetsQuerySetsPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsQuerySetsPutResponses = {
    /**
     * Successful operation
     */
    200: AssetSavedQuerySet;
};

export type AssetsQuerySetsPutResponse = AssetsQuerySetsPutResponses[keyof AssetsQuerySetsPutResponses];

export type AssetsServicesGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/assets/services';
};

export type AssetsServicesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsServicesGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<AssetService>;
    };
};

export type AssetsServicesGetResponse = AssetsServicesGetResponses[keyof AssetsServicesGetResponses];

export type AssetsServicesGroupedGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/assets/services/grouped';
};

export type AssetsServicesGroupedGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsServicesGroupedGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<AssetServiceGrouped>;
    };
};

export type AssetsServicesGroupedGetResponse = AssetsServicesGroupedGetResponses[keyof AssetsServicesGroupedGetResponses];

export type AssetsToTagsPostData = {
    body?: AssetToTag;
    path?: never;
    query?: never;
    url: '/assets/to-tags';
};

export type AssetsToTagsPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsToTagsPostResponses = {
    /**
     * Successful operation
     */
    201: AssetToTag;
};

export type AssetsToTagsPostResponse = AssetsToTagsPostResponses[keyof AssetsToTagsPostResponses];

export type AssetsToTagsPutData = {
    body?: AssetToTag;
    path?: never;
    query?: never;
    url: '/assets/to-tags';
};

export type AssetsToTagsPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsToTagsPutResponses = {
    /**
     * Successful operation
     */
    200: AssetToTag;
};

export type AssetsToTagsPutResponse = AssetsToTagsPutResponses[keyof AssetsToTagsPutResponses];

export type AssetsTagsDeleteData = {
    body?: {
        id: number;
    };
    path?: never;
    query?: never;
    url: '/assets/tags';
};

export type AssetsTagsDeleteErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsTagsDeleteResponses = {
    /**
     * Successful operation
     */
    200: DeleteResponse;
};

export type AssetsTagsDeleteResponse = AssetsTagsDeleteResponses[keyof AssetsTagsDeleteResponses];

export type AssetsTagsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/assets/tags';
};

export type AssetsTagsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsTagsGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<AssetTag>;
    };
};

export type AssetsTagsGetResponse = AssetsTagsGetResponses[keyof AssetsTagsGetResponses];

export type AssetsTagsPostData = {
    body?: AssetTag;
    path?: never;
    query?: never;
    url: '/assets/tags';
};

export type AssetsTagsPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsTagsPostResponses = {
    /**
     * Successful operation
     */
    201: AssetTag;
};

export type AssetsTagsPostResponse = AssetsTagsPostResponses[keyof AssetsTagsPostResponses];

export type AssetsTagsPutData = {
    body?: AssetTag;
    path?: never;
    query?: never;
    url: '/assets/tags';
};

export type AssetsTagsPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsTagsPutResponses = {
    /**
     * Successful operation
     */
    200: AssetTag;
};

export type AssetsTagsPutResponse = AssetsTagsPutResponses[keyof AssetsTagsPutResponses];

export type AssetsTagsTypesDeleteData = {
    body?: {
        id: number;
    };
    path?: never;
    query?: never;
    url: '/assets/tags/types';
};

export type AssetsTagsTypesDeleteErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsTagsTypesDeleteResponses = {
    /**
     * Successful operation
     */
    200: DeleteResponse;
};

export type AssetsTagsTypesDeleteResponse = AssetsTagsTypesDeleteResponses[keyof AssetsTagsTypesDeleteResponses];

export type AssetsTagsTypesGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/assets/tags/types';
};

export type AssetsTagsTypesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsTagsTypesGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<AssetTagType>;
    };
};

export type AssetsTagsTypesGetResponse = AssetsTagsTypesGetResponses[keyof AssetsTagsTypesGetResponses];

export type AssetsTagsTypesPostData = {
    body?: AssetTagType;
    path?: never;
    query?: never;
    url: '/assets/tags/types';
};

export type AssetsTagsTypesPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsTagsTypesPostResponses = {
    /**
     * Successful operation
     */
    200: AssetTagType;
};

export type AssetsTagsTypesPostResponse = AssetsTagsTypesPostResponses[keyof AssetsTagsTypesPostResponses];

export type AssetsTagsTypesPutData = {
    body?: AssetTagType;
    path?: never;
    query?: never;
    url: '/assets/tags/types';
};

export type AssetsTagsTypesPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type AssetsTagsTypesPutResponses = {
    /**
     * Successful operation
     */
    200: AssetTagType;
};

export type AssetsTagsTypesPutResponse = AssetsTagsTypesPutResponses[keyof AssetsTagsTypesPutResponses];

export type ClosureCategoriesGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
    };
    url: '/closure_categories';
};

export type ClosureCategoriesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type ClosureCategoriesGetResponses = {
    /**
     * Successful operation
     */
    200: Array<ClosureCategory>;
};

export type ClosureCategoriesGetResponse = ClosureCategoriesGetResponses[keyof ClosureCategoriesGetResponses];

export type ClosureCategoriesIdGetData = {
    body?: never;
    path: {
        id: string;
    };
    query?: never;
    url: '/closure_categories/{id}';
};

export type ClosureCategoriesIdGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type ClosureCategoriesIdGetResponses = {
    /**
     * Successful operation
     */
    200: ClosureCategory;
};

export type ClosureCategoriesIdGetResponse = ClosureCategoriesIdGetResponses[keyof ClosureCategoriesIdGetResponses];

export type ClosureCodesGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
    };
    url: '/closure_codes';
};

export type ClosureCodesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type ClosureCodesGetResponses = {
    /**
     * Successful operation
     */
    200: Array<ClosureCode>;
};

export type ClosureCodesGetResponse = ClosureCodesGetResponses[keyof ClosureCodesGetResponses];

export type ClosureCodesIdGetData = {
    body?: never;
    path: {
        id: string;
    };
    query?: never;
    url: '/closure_codes/{id}';
};

export type ClosureCodesIdGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type ClosureCodesIdGetResponses = {
    /**
     * Successful operation
     */
    200: ClosureCode;
};

export type ClosureCodesIdGetResponse = ClosureCodesIdGetResponses[keyof ClosureCodesIdGetResponses];

export type ContactPostData = {
    body?: {
        name: string;
        email: string;
        request_type: string;
        problem?: string;
        solution?: string;
        context?: string;
        file?: Blob | File;
    };
    path?: never;
    query?: never;
    url: '/contact';
};

export type ContactPostResponses = {
    /**
     * Successful operation
     */
    200: {
        success?: boolean;
    };
};

export type ContactPostResponse = ContactPostResponses[keyof ContactPostResponses];

export type FindingsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
        id?: string;
        collision_slug?: string;
        detection_version?: string;
        detection_name?: Array<string>;
        severity?: Array<string>;
        first_event_timestamp__gte?: Date;
        first_event_timestamp__lte?: Date;
        last_event_timestamp__gte?: Date;
        last_event_timestamp__lte?: Date;
        last_analysis_timestamp?: string;
        time_since?: string;
        status?: Array<string>;
        assignee?: Array<string>;
    };
    url: '/findings';
};

export type FindingsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type FindingsGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<Finding>;
    };
};

export type FindingsGetResponse = FindingsGetResponses[keyof FindingsGetResponses];

export type FindingsIdGetData = {
    body?: never;
    path: {
        /**
         * Unique id for the finding
         */
        id: string;
    };
    query?: {
        /**
         * If supplied, only objects after since, as related to Finding, will be included in the response
         */
        since?: Date;
    };
    url: '/findings/{id}';
};

export type FindingsIdGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type FindingsIdGetResponses = {
    /**
     * Successful operation
     */
    200: FullFinding;
};

export type FindingsIdGetResponse = FindingsIdGetResponses[keyof FindingsIdGetResponses];

export type FindingHistoryGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/finding_history';
};

export type FindingHistoryGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type FindingHistoryGetResponses = {
    /**
     * Successful operation
     */
    200: Array<FindingHistoryLimited>;
};

export type FindingHistoryGetResponse = FindingHistoryGetResponses[keyof FindingHistoryGetResponses];

export type GeminiGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/gemini';
};

export type GeminiGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type GeminiGetResponses = {
    /**
     * Successful operation
     */
    200: GeminiSettings;
};

export type GeminiGetResponse = GeminiGetResponses[keyof GeminiGetResponses];

export type GeminiPostData = {
    body: {
        emails: Array<string>;
        sla_map: {
            [key: string]: number;
        };
        escalation_map: {
            [key: string]: Array<string>;
        };
        notification_map: {
            informational?: Array<CommunicationMethod>;
            low?: Array<CommunicationMethod>;
            medium?: Array<CommunicationMethod>;
            high?: Array<CommunicationMethod>;
            critical?: Array<CommunicationMethod>;
        };
        escalations_enabled?: boolean;
        escalation_rollup?: boolean;
        email_summary_enabled?: boolean;
        slack_enabled?: boolean;
        email_enabled?: boolean;
    };
    path?: never;
    query?: never;
    url: '/gemini';
};

export type GeminiPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type GeminiPostResponses = {
    /**
     * Successful operation
     */
    200: {
        success?: boolean;
        created?: boolean;
        updated?: boolean;
    };
};

export type GeminiPostResponse = GeminiPostResponses[keyof GeminiPostResponses];

export type GeminiEmailsPatchData = {
    body: {
        add?: Array<string>;
        remove?: Array<string>;
    };
    path?: never;
    query?: never;
    url: '/gemini/emails';
};

export type GeminiEmailsPatchErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type GeminiEmailsPatchResponses = {
    /**
     * Successful operation
     */
    200: {
        emails: Array<string>;
    };
};

export type GeminiEmailsPatchResponse = GeminiEmailsPatchResponses[keyof GeminiEmailsPatchResponses];

export type HuntReportsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
    };
    url: '/hunt_reports';
};

export type HuntReportsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type HuntReportsGetResponses = {
    /**
     * Successful operation
     */
    200: HuntReportList;
};

export type HuntReportsGetResponse = HuntReportsGetResponses[keyof HuntReportsGetResponses];

export type HuntReportsFileNameGetData = {
    body?: never;
    path: {
        /**
         * Unique id for the finding
         */
        file_name: string;
    };
    query?: never;
    url: '/hunt_reports/{file_name}';
};

export type HuntReportsFileNameGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type HuntReportsFileNameGetResponses = {
    /**
     * Successful operation
     */
    200: Blob | File;
};

export type HuntReportsFileNameGetResponse = HuntReportsFileNameGetResponses[keyof HuntReportsFileNameGetResponses];

export type IdsEventsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
        id?: string;
        signature?: Array<string>;
        signature_id?: Array<number>;
        severity?: Array<string>;
        category?: Array<string>;
        protocol?: Array<string>;
        sensor_name?: Array<string>;
        src_ip?: Array<string>;
        dst_ip?: Array<string>;
        vlan_id?: Array<number>;
    };
    url: '/ids_events';
};

export type IdsEventsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type IdsEventsGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<SuricataEvent>;
    };
};

export type IdsEventsGetResponse = IdsEventsGetResponses[keyof IdsEventsGetResponses];

export type IdsEventsGroupedCategoriesGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
        signature?: Array<string>;
        signature_id?: Array<number>;
        severity?: Array<string>;
        category?: Array<string>;
        protocol?: Array<string>;
        sensor_name?: Array<string>;
        src_ip?: Array<string>;
        dst_ip?: Array<string>;
        vlan_id?: Array<number>;
    };
    url: '/ids_events/grouped/categories';
};

export type IdsEventsGroupedCategoriesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type IdsEventsGroupedCategoriesGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<SuricataCategoryGroup>;
    };
};

export type IdsEventsGroupedCategoriesGetResponse = IdsEventsGroupedCategoriesGetResponses[keyof IdsEventsGroupedCategoriesGetResponses];

export type IdsEventsGroupedRulesGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
        signature?: Array<string>;
        signature_id?: Array<number>;
        severity?: Array<string>;
        category?: Array<string>;
        protocol?: Array<string>;
        sensor_name?: Array<string>;
        src_ip?: Array<string>;
        dst_ip?: Array<string>;
        vlan_id?: Array<number>;
    };
    url: '/ids_events/grouped/rules';
};

export type IdsEventsGroupedRulesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type IdsEventsGroupedRulesGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<SuricataRuleGroup>;
    };
};

export type IdsEventsGroupedRulesGetResponse = IdsEventsGroupedRulesGetResponses[keyof IdsEventsGroupedRulesGetResponses];

export type IdsEventsIdGetData = {
    body?: never;
    path: {
        /**
         * Unique id for the IDS event
         */
        id: string;
    };
    query?: never;
    url: '/ids_events/{id}';
};

export type IdsEventsIdGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type IdsEventsIdGetResponses = {
    /**
     * Successful operation
     */
    200: SuricataEvent;
};

export type IdsEventsIdGetResponse = IdsEventsIdGetResponses[keyof IdsEventsIdGetResponses];

export type ReportsGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/reports';
};

export type ReportsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type ReportsGetResponses = {
    /**
     * Successful operation
     */
    200: ReportList;
};

export type ReportsGetResponse = ReportsGetResponses[keyof ReportsGetResponses];

export type ReportsFindingsPostData = {
    /**
     * The input types are dictated per a JSONSchema per report
     */
    body: {
        age?: string;
        end_date?: Date;
    };
    path?: never;
    query?: never;
    url: '/reports/findings';
};

export type ReportsFindingsPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type ReportsFindingsPostResponses = {
    /**
     * Successful operation
     */
    200: Blob | File;
};

export type ReportsFindingsPostResponse = ReportsFindingsPostResponses[keyof ReportsFindingsPostResponses];

export type RollupsAffectedAssetsByDayGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Start date
         */
        start?: Date;
        /**
         * End date
         */
        end?: Date;
    };
    url: '/rollups/affected-assets-by-day';
};

export type RollupsAffectedAssetsByDayGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type RollupsAffectedAssetsByDayGetResponses = {
    /**
     * Successful operation
     */
    200: Array<AssetsByDayRollupElement>;
};

export type RollupsAffectedAssetsByDayGetResponse = RollupsAffectedAssetsByDayGetResponses[keyof RollupsAffectedAssetsByDayGetResponses];

export type RollupsAssetsByDayGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Start date
         */
        start?: Date;
        /**
         * End date
         */
        end?: Date;
        /**
         * Valid timezone string, used to determine time windows
         */
        timezone?: string;
    };
    url: '/rollups/assets-by-day';
};

export type RollupsAssetsByDayGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type RollupsAssetsByDayGetResponses = {
    /**
     * Successful operation
     */
    200: Array<AssetsByDayRollupElement>;
};

export type RollupsAssetsByDayGetResponse = RollupsAssetsByDayGetResponses[keyof RollupsAssetsByDayGetResponses];

export type RollupsDashboardGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Age to show in dashboard
         */
        age?: '1 week' | '1 month' | '3 months';
        /**
         * Window size for timeline
         */
        window?: '1 hour' | '1 day' | '1 week';
        /**
         * How times should be truncated; Should match `window`
         */
        truncate_to?: 'hour' | 'day' | 'week';
        /**
         * Valid timezone string, used to determine time windows
         */
        timezone?: string;
    };
    url: '/rollups/dashboard';
};

export type RollupsDashboardGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type RollupsDashboardGetResponses = {
    /**
     * Successful operation
     */
    200: DashboardRollup;
};

export type RollupsDashboardGetResponse = RollupsDashboardGetResponses[keyof RollupsDashboardGetResponses];

export type RollupsDataTransferByDayGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Start date
         */
        start?: Date;
        /**
         * End date
         */
        end?: Date;
        /**
         * Valid timezone string, used to determine time windows
         */
        timezone?: string;
    };
    url: '/rollups/data-transfer-by-day';
};

export type RollupsDataTransferByDayGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type RollupsDataTransferByDayGetResponses = {
    /**
     * Successful operation
     */
    200: Array<DataTransferByDayRollupElement>;
};

export type RollupsDataTransferByDayGetResponse = RollupsDataTransferByDayGetResponses[keyof RollupsDataTransferByDayGetResponses];

export type SensorsGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/sensors';
};

export type SensorsGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
    };
};

export type SensorsGetResponse = SensorsGetResponses[keyof SensorsGetResponses];

export type VrfsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/vrfs';
};

export type VrfsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type VrfsGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<Vrf>;
    };
};

export type VrfsGetResponse = VrfsGetResponses[keyof VrfsGetResponses];

export type VrfsPostData = {
    body: VrfInput;
    path?: never;
    query?: never;
    url: '/vrfs';
};

export type VrfsPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type VrfsPostResponses = {
    /**
     * Created
     */
    201: Vrf;
};

export type VrfsPostResponse = VrfsPostResponses[keyof VrfsPostResponses];

export type VrfsPutData = {
    body: Vrf;
    path?: never;
    query?: never;
    url: '/vrfs';
};

export type VrfsPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type VrfsPutResponses = {
    /**
     * Successful operation
     */
    200: Vrf;
};

export type VrfsPutResponse = VrfsPutResponses[keyof VrfsPutResponses];

export type VrfsToTagsDeleteData = {
    body?: {
        id: number;
    };
    path?: never;
    query?: never;
    url: '/vrfs/to-tags';
};

export type VrfsToTagsDeleteErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type VrfsToTagsDeleteResponses = {
    /**
     * Successful operation
     */
    200: DeleteResponse;
};

export type VrfsToTagsDeleteResponse = VrfsToTagsDeleteResponses[keyof VrfsToTagsDeleteResponses];

export type VrfsToTagsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/vrfs/to-tags';
};

export type VrfsToTagsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type VrfsToTagsGetResponses = {
    /**
     * Successful operation
     */
    200: Array<VrfToTag>;
};

export type VrfsToTagsGetResponse = VrfsToTagsGetResponses[keyof VrfsToTagsGetResponses];

export type VrfsToTagsPostData = {
    body?: VrfToTag;
    path?: never;
    query?: never;
    url: '/vrfs/to-tags';
};

export type VrfsToTagsPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type VrfsToTagsPostResponses = {
    /**
     * Successful operation
     */
    201: VrfToTag;
};

export type VrfsToTagsPostResponse = VrfsToTagsPostResponses[keyof VrfsToTagsPostResponses];

export type VrfsIdGetData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/vrfs/{id}';
};

export type VrfsIdGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type VrfsIdGetResponses = {
    /**
     * Successful operation
     */
    200: Vrf;
};

export type VrfsIdGetResponse = VrfsIdGetResponses[keyof VrfsIdGetResponses];

export type VlansGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/vlans';
};

export type VlansGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type VlansGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<Vlan>;
    };
};

export type VlansGetResponse = VlansGetResponses[keyof VlansGetResponses];

export type VlansPostData = {
    body: VlanInput;
    path?: never;
    query?: never;
    url: '/vlans';
};

export type VlansPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type VlansPostResponses = {
    /**
     * Created
     */
    201: Vlan;
};

export type VlansPostResponse = VlansPostResponses[keyof VlansPostResponses];

export type VlansPutData = {
    body: Vlan;
    path?: never;
    query?: never;
    url: '/vlans';
};

export type VlansPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type VlansPutResponses = {
    /**
     * Successful operation
     */
    200: Vlan;
};

export type VlansPutResponse = VlansPutResponses[keyof VlansPutResponses];

export type VlansIdGetData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/vlans/{id}';
};

export type VlansIdGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type VlansIdGetResponses = {
    /**
     * Successful operation
     */
    200: Vlan;
};

export type VlansIdGetResponse = VlansIdGetResponses[keyof VlansIdGetResponses];

export type NetworkPrefixesGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/network_prefixes';
};

export type NetworkPrefixesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type NetworkPrefixesGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<NetworkPrefix>;
    };
};

export type NetworkPrefixesGetResponse = NetworkPrefixesGetResponses[keyof NetworkPrefixesGetResponses];

export type NetworkPrefixesPostData = {
    body: NetworkPrefixInput;
    path?: never;
    query?: never;
    url: '/network_prefixes';
};

export type NetworkPrefixesPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type NetworkPrefixesPostResponses = {
    /**
     * Created
     */
    201: NetworkPrefix;
};

export type NetworkPrefixesPostResponse = NetworkPrefixesPostResponses[keyof NetworkPrefixesPostResponses];

export type NetworkPrefixesPutData = {
    body: NetworkPrefix;
    path?: never;
    query?: never;
    url: '/network_prefixes';
};

export type NetworkPrefixesPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type NetworkPrefixesPutResponses = {
    /**
     * Successful operation
     */
    200: NetworkPrefix;
};

export type NetworkPrefixesPutResponse = NetworkPrefixesPutResponses[keyof NetworkPrefixesPutResponses];

export type NetworkPrefixesToTagsDeleteData = {
    body?: {
        id: number;
    };
    path?: never;
    query?: never;
    url: '/network_prefixes/to-tags';
};

export type NetworkPrefixesToTagsDeleteErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type NetworkPrefixesToTagsDeleteResponses = {
    /**
     * Successful operation
     */
    200: DeleteResponse;
};

export type NetworkPrefixesToTagsDeleteResponse = NetworkPrefixesToTagsDeleteResponses[keyof NetworkPrefixesToTagsDeleteResponses];

export type NetworkPrefixesToTagsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/network_prefixes/to-tags';
};

export type NetworkPrefixesToTagsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type NetworkPrefixesToTagsGetResponses = {
    /**
     * Successful operation
     */
    200: Array<NetworkPrefixToTag>;
};

export type NetworkPrefixesToTagsGetResponse = NetworkPrefixesToTagsGetResponses[keyof NetworkPrefixesToTagsGetResponses];

export type NetworkPrefixesToTagsPostData = {
    body?: NetworkPrefixToTag;
    path?: never;
    query?: never;
    url: '/network_prefixes/to-tags';
};

export type NetworkPrefixesToTagsPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type NetworkPrefixesToTagsPostResponses = {
    /**
     * Successful operation
     */
    201: NetworkPrefixToTag;
};

export type NetworkPrefixesToTagsPostResponse = NetworkPrefixesToTagsPostResponses[keyof NetworkPrefixesToTagsPostResponses];

export type NetworkPrefixesIdGetData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/network_prefixes/{id}';
};

export type NetworkPrefixesIdGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type NetworkPrefixesIdGetResponses = {
    /**
     * Successful operation
     */
    200: NetworkPrefix;
};

export type NetworkPrefixesIdGetResponse = NetworkPrefixesIdGetResponses[keyof NetworkPrefixesIdGetResponses];

export type BuildingsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/buildings';
};

export type BuildingsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type BuildingsGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<Building>;
    };
};

export type BuildingsGetResponse = BuildingsGetResponses[keyof BuildingsGetResponses];

export type BuildingsPostData = {
    body: BuildingInput;
    path?: never;
    query?: never;
    url: '/buildings';
};

export type BuildingsPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type BuildingsPostResponses = {
    /**
     * Created
     */
    201: Building;
};

export type BuildingsPostResponse = BuildingsPostResponses[keyof BuildingsPostResponses];

export type BuildingsPutData = {
    body: Building;
    path?: never;
    query?: never;
    url: '/buildings';
};

export type BuildingsPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type BuildingsPutResponses = {
    /**
     * Successful operation
     */
    200: Building;
};

export type BuildingsPutResponse = BuildingsPutResponses[keyof BuildingsPutResponses];

export type CustomAssetAttributesDeleteData = {
    body?: {
        id: number;
    };
    path?: never;
    query?: never;
    url: '/custom_asset_attributes';
};

export type CustomAssetAttributesDeleteErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type CustomAssetAttributesDeleteResponses = {
    /**
     * Successful operation
     */
    200: DeleteResponse;
};

export type CustomAssetAttributesDeleteResponse = CustomAssetAttributesDeleteResponses[keyof CustomAssetAttributesDeleteResponses];

export type CustomAssetAttributesGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/custom_asset_attributes';
};

export type CustomAssetAttributesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type CustomAssetAttributesGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<CustomAssetAttributeDefinition>;
    };
};

export type CustomAssetAttributesGetResponse = CustomAssetAttributesGetResponses[keyof CustomAssetAttributesGetResponses];

export type CustomAssetAttributesPostData = {
    body: CustomAssetAttributeDefinitionInput;
    path?: never;
    query?: never;
    url: '/custom_asset_attributes';
};

export type CustomAssetAttributesPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type CustomAssetAttributesPostResponses = {
    /**
     * Created
     */
    201: CustomAssetAttributeDefinition;
};

export type CustomAssetAttributesPostResponse = CustomAssetAttributesPostResponses[keyof CustomAssetAttributesPostResponses];

export type CustomAssetAttributesPutData = {
    body: CustomAssetAttributeDefinition;
    path?: never;
    query?: never;
    url: '/custom_asset_attributes';
};

export type CustomAssetAttributesPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type CustomAssetAttributesPutResponses = {
    /**
     * Successful operation
     */
    200: CustomAssetAttributeDefinition;
};

export type CustomAssetAttributesPutResponse = CustomAssetAttributesPutResponses[keyof CustomAssetAttributesPutResponses];

export type PcapUploadsPresignPostData = {
    body: PcapUploadPresignInput;
    path?: never;
    query?: never;
    url: '/pcap_uploads/presign';
};

export type PcapUploadsPresignPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type PcapUploadsPresignPostResponses = {
    /**
     * Successful operation
     */
    200: PcapUploadPresignOutput;
};

export type PcapUploadsPresignPostResponse = PcapUploadsPresignPostResponses[keyof PcapUploadsPresignPostResponses];

export type PcapUploadsProcessPostData = {
    body: PcapUploadProcessInput;
    path?: never;
    query?: never;
    url: '/pcap_uploads/process';
};

export type PcapUploadsProcessPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type PcapUploadsProcessPostResponses = {
    /**
     * Job created
     */
    202: unknown;
};

export type BuildingsIdGetData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/buildings/{id}';
};

export type BuildingsIdGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type BuildingsIdGetResponses = {
    /**
     * Successful operation
     */
    200: Building;
};

export type BuildingsIdGetResponse = BuildingsIdGetResponses[keyof BuildingsIdGetResponses];

export type GalvanickAppliancesGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/galvanick_appliances';
};

export type GalvanickAppliancesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type GalvanickAppliancesGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<GalvanickAppliance>;
    };
};

export type GalvanickAppliancesGetResponse = GalvanickAppliancesGetResponses[keyof GalvanickAppliancesGetResponses];

export type GalvanickAppliancesPostData = {
    body: GalvanickApplianceInput;
    path?: never;
    query?: never;
    url: '/galvanick_appliances';
};

export type GalvanickAppliancesPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type GalvanickAppliancesPostResponses = {
    /**
     * Created
     */
    201: GalvanickAppliance;
};

export type GalvanickAppliancesPostResponse = GalvanickAppliancesPostResponses[keyof GalvanickAppliancesPostResponses];

export type GalvanickAppliancesPutData = {
    body: GalvanickAppliance;
    path?: never;
    query?: never;
    url: '/galvanick_appliances';
};

export type GalvanickAppliancesPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type GalvanickAppliancesPutResponses = {
    /**
     * Successful operation
     */
    200: GalvanickAppliance;
};

export type GalvanickAppliancesPutResponse = GalvanickAppliancesPutResponses[keyof GalvanickAppliancesPutResponses];

export type GalvanickAppliancesIdGetData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/galvanick_appliances/{id}';
};

export type GalvanickAppliancesIdGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type GalvanickAppliancesIdGetResponses = {
    /**
     * Successful operation
     */
    200: GalvanickAppliance;
};

export type GalvanickAppliancesIdGetResponse = GalvanickAppliancesIdGetResponses[keyof GalvanickAppliancesIdGetResponses];

export type EndpointSensorsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/endpoint_sensors';
};

export type EndpointSensorsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type EndpointSensorsGetResponses = {
    /**
     * Successful operation
     */
    200: {
        count?: number;
        results?: Array<EndpointSensor>;
    };
};

export type EndpointSensorsGetResponse = EndpointSensorsGetResponses[keyof EndpointSensorsGetResponses];

export type EndpointSensorsPutData = {
    body: EndpointSensorWritable;
    path?: never;
    query?: never;
    url: '/endpoint_sensors';
};

export type EndpointSensorsPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type EndpointSensorsPutResponses = {
    /**
     * Successful operation
     */
    200: EndpointSensor;
};

export type EndpointSensorsPutResponse = EndpointSensorsPutResponses[keyof EndpointSensorsPutResponses];

export type EndpointSensorsIdGetData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/endpoint_sensors/{id}';
};

export type EndpointSensorsIdGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type EndpointSensorsIdGetResponses = {
    /**
     * Successful operation
     */
    200: EndpointSensor;
};

export type EndpointSensorsIdGetResponse = EndpointSensorsIdGetResponses[keyof EndpointSensorsIdGetResponses];

export type EndpointLogSourcesGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/endpoint_log_sources';
};

export type EndpointLogSourcesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type EndpointLogSourcesGetResponses = {
    /**
     * Successful operation
     */
    200: Array<EndpointLogSource>;
};

export type EndpointLogSourcesGetResponse = EndpointLogSourcesGetResponses[keyof EndpointLogSourcesGetResponses];

export type EndpointLogSourcesPutData = {
    body: EndpointLogSourceInput;
    path?: never;
    query?: never;
    url: '/endpoint_log_sources';
};

export type EndpointLogSourcesPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type EndpointLogSourcesPutResponses = {
    /**
     * Successful operation
     */
    200: EndpointLogSource;
};

export type EndpointLogSourcesPutResponse = EndpointLogSourcesPutResponses[keyof EndpointLogSourcesPutResponses];

export type GalvanickAppliancesLocalPrefixesDeleteData = {
    body?: {
        id: number;
    };
    path?: never;
    query?: never;
    url: '/galvanick_appliances/local_prefixes';
};

export type GalvanickAppliancesLocalPrefixesDeleteErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type GalvanickAppliancesLocalPrefixesDeleteResponses = {
    /**
     * Successful operation
     */
    200: DeleteResponse;
};

export type GalvanickAppliancesLocalPrefixesDeleteResponse = GalvanickAppliancesLocalPrefixesDeleteResponses[keyof GalvanickAppliancesLocalPrefixesDeleteResponses];

export type GalvanickAppliancesLocalPrefixesPostData = {
    body?: NetworkSensorLocalPrefix;
    path?: never;
    query?: never;
    url: '/galvanick_appliances/local_prefixes';
};

export type GalvanickAppliancesLocalPrefixesPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type GalvanickAppliancesLocalPrefixesPostResponses = {
    /**
     * Successful operation
     */
    201: NetworkSensorLocalPrefix;
};

export type GalvanickAppliancesLocalPrefixesPostResponse = GalvanickAppliancesLocalPrefixesPostResponses[keyof GalvanickAppliancesLocalPrefixesPostResponses];

export type TicketStatusChangesGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/ticket_status_changes';
};

export type TicketStatusChangesGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type TicketStatusChangesGetResponses = {
    /**
     * Successful operation
     */
    200: Array<TicketStatusChange>;
};

export type TicketStatusChangesGetResponse = TicketStatusChangesGetResponses[keyof TicketStatusChangesGetResponses];

export type TicketsCommentGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filters, one query parameter per condition: `field=value`, or `field__operator=value` with an operator of `ne`, `gt`, `gte`, `lt`, `lte`, `like`, `ilike`, `notlike`, `notilike`, `isnull` or `isnotnull`. See Filtering in the introduction for what each operator does.
         *
         */
        queryables?: Queryables;
        /**
         * Maximum number of items to return.
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * Field name to use for ordering list results
         */
        order_by?: string;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/tickets/comment';
};

export type TicketsCommentGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type TicketsCommentGetResponses = {
    /**
     * Successful operation
     */
    200: Array<TicketComment>;
};

export type TicketsCommentGetResponse = TicketsCommentGetResponses[keyof TicketsCommentGetResponses];

export type TicketsCommentPostData = {
    body?: TicketCommentInput;
    path?: never;
    query?: never;
    url: '/tickets/comment';
};

export type TicketsCommentPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type TicketsCommentPostResponses = {
    /**
     * Successful operation
     */
    200: TicketComment;
};

export type TicketsCommentPostResponse = TicketsCommentPostResponses[keyof TicketsCommentPostResponses];

export type TicketsHistoryPostData = {
    body?: TicketHistory;
    path?: never;
    query?: never;
    url: '/tickets/history';
};

export type TicketsHistoryPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type TicketsHistoryPostResponses = {
    /**
     * Successful operation
     */
    200: TicketHistory;
};

export type TicketsHistoryPostResponse = TicketsHistoryPostResponses[keyof TicketsHistoryPostResponses];

export type UsersGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users';
};

export type UsersGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersGetResponses = {
    /**
     * Successful operation
     */
    200: Array<User>;
};

export type UsersGetResponse = UsersGetResponses[keyof UsersGetResponses];

export type UsersPostData = {
    body: NewUser;
    path?: never;
    query?: never;
    url: '/users';
};

export type UsersPostErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * The request is not applicable to the resource in its current state. Distinct from 403: the caller holds the access, but the operation does not apply.
     *
     */
    409: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Not available in this deployment
     */
    501: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersPostResponses = {
    /**
     * User created, temporary password returned
     */
    201: CreatedUser;
};

export type UsersPostResponse = UsersPostResponses[keyof UsersPostResponses];

export type UsersEventsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Event types to return, repeated for more than one — `?type=LOGIN&type=LOGIN_ERROR`. Empty returns every type these endpoints report, which GET /users/events/types lists. A type outside that set is refused with 400.
         *
         */
        type?: Array<UserEventType>;
        /**
         * Only return events at or after this instant, RFC 3339. Open ended when omitted, and held to the 30-day window the identity provider retains: an earlier value returns what is there rather than failing.
         *
         */
        date_from?: Date;
        /**
         * Only return events at or before this instant, RFC 3339. Open ended when omitted, so the newest events are returned.
         *
         */
        date_to?: Date;
        /**
         * Maximum number of events to return. Allowed higher than the limit on the other lists: an event is a fraction of the size of the records those page, and reading them costs one request to the identity provider however many are asked for.
         *
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/users/events';
};

export type UsersEventsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Not available in this deployment
     */
    501: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersEventsGetResponses = {
    /**
     * Successful operation
     */
    200: Array<UserEvent>;
};

export type UsersEventsGetResponse = UsersEventsGetResponses[keyof UsersEventsGetResponses];

export type UsersEventTypesGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/events/types';
};

export type UsersEventTypesGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Not available in this deployment
     */
    501: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersEventTypesGetResponses = {
    /**
     * Successful operation
     */
    200: Array<UserEventType>;
};

export type UsersEventTypesGetResponse = UsersEventTypesGetResponses[keyof UsersEventTypesGetResponses];

export type UsersGroupsGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/groups';
};

export type UsersGroupsGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Not available in this deployment
     */
    501: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersGroupsGetResponses = {
    /**
     * Successful operation
     */
    200: Array<PortalGroup>;
};

export type UsersGroupsGetResponse = UsersGroupsGetResponses[keyof UsersGroupsGetResponses];

export type UsersOrganizationsGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/organizations';
};

export type UsersOrganizationsGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Not available in this deployment
     */
    501: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersOrganizationsGetResponses = {
    /**
     * Successful operation
     */
    200: Array<OrganizationSummary>;
};

export type UsersOrganizationsGetResponse = UsersOrganizationsGetResponses[keyof UsersOrganizationsGetResponses];

export type UsersOrganizationsIdGetData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/users/organizations/{id}';
};

export type UsersOrganizationsIdGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Not available in this deployment
     */
    501: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersOrganizationsIdGetResponses = {
    /**
     * Successful operation
     */
    200: OrganizationDetail;
};

export type UsersOrganizationsIdGetResponse = UsersOrganizationsIdGetResponses[keyof UsersOrganizationsIdGetResponses];

export type UsersIdGetData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/users/{id}';
};

export type UsersIdGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Not available in this deployment
     */
    501: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersIdGetResponses = {
    /**
     * Successful operation
     */
    200: UserDetail;
};

export type UsersIdGetResponse = UsersIdGetResponses[keyof UsersIdGetResponses];

export type UsersIdPatchData = {
    body: UserUpdate;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/users/{id}';
};

export type UsersIdPatchErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * The request is not applicable to the resource in its current state. Distinct from 403: the caller holds the access, but the operation does not apply.
     *
     */
    409: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Not available in this deployment
     */
    501: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersIdPatchResponses = {
    /**
     * User updated
     */
    200: UserDetail;
};

export type UsersIdPatchResponse = UsersIdPatchResponses[keyof UsersIdPatchResponses];

export type UsersEnabledPutData = {
    body: UserEnabledInput;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/users/{id}/enabled';
};

export type UsersEnabledPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Not available in this deployment
     */
    501: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersEnabledPutResponses = {
    /**
     * Account state set
     */
    200: UserDetail;
};

export type UsersEnabledPutResponse = UsersEnabledPutResponses[keyof UsersEnabledPutResponses];

export type UsersIdEventsGetData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: {
        /**
         * Event types to return, repeated for more than one — `?type=LOGIN&type=LOGIN_ERROR`. Empty returns every type these endpoints report, which GET /users/events/types lists. A type outside that set is refused with 400.
         *
         */
        type?: Array<UserEventType>;
        /**
         * Only return events at or after this instant, RFC 3339. Open ended when omitted, and held to the 30-day window the identity provider retains: an earlier value returns what is there rather than failing.
         *
         */
        date_from?: Date;
        /**
         * Only return events at or before this instant, RFC 3339. Open ended when omitted, so the newest events are returned.
         *
         */
        date_to?: Date;
        /**
         * Maximum number of events to return. Allowed higher than the limit on the other lists: an event is a fraction of the size of the records those page, and reading them costs one request to the identity provider however many are asked for.
         *
         */
        limit?: number;
        /**
         * Page number
         */
        page?: number;
        /**
         * If present and true, list results will be ordered descending
         */
        order_is_desc?: boolean;
    };
    url: '/users/{id}/events';
};

export type UsersIdEventsGetErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Not available in this deployment
     */
    501: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersIdEventsGetResponses = {
    /**
     * Successful operation
     */
    200: Array<UserEvent>;
};

export type UsersIdEventsGetResponse = UsersIdEventsGetResponses[keyof UsersIdEventsGetResponses];

export type UsersGroupDeleteData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
        /**
         * ID of a portal group, as returned by GET /users/groups. Any other value is rejected with 400.
         *
         */
        group_id: string;
    };
    query?: never;
    url: '/users/{id}/groups/{group_id}';
};

export type UsersGroupDeleteErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Not available in this deployment
     */
    501: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersGroupDeleteResponses = {
    /**
     * Membership removed
     */
    204: void;
};

export type UsersGroupDeleteResponse = UsersGroupDeleteResponses[keyof UsersGroupDeleteResponses];

export type UsersGroupPutData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
        /**
         * ID of a portal group, as returned by GET /users/groups. Any other value is rejected with 400.
         *
         */
        group_id: string;
    };
    query?: never;
    url: '/users/{id}/groups/{group_id}';
};

export type UsersGroupPutErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Not available in this deployment
     */
    501: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersGroupPutResponses = {
    /**
     * Membership added
     */
    204: void;
};

export type UsersGroupPutResponse = UsersGroupPutResponses[keyof UsersGroupPutResponses];

export type UsersPasswordPostData = {
    body?: never;
    path: {
        /**
         * The resource's unique identifier
         */
        id: string;
    };
    query?: never;
    url: '/users/{id}/password';
};

export type UsersPasswordPostErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * The request is not applicable to the resource in its current state. Distinct from 403: the caller holds the access, but the operation does not apply.
     *
     */
    409: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Not available in this deployment
     */
    501: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type UsersPasswordPostResponses = {
    /**
     * Password reset, temporary password returned
     */
    200: TemporaryPassword;
};

export type UsersPasswordPostResponse = UsersPasswordPostResponses[keyof UsersPasswordPostResponses];

export type ValuesGetData = {
    body?: never;
    path?: never;
    query: {
        key: Array<string>;
    };
    url: '/values';
};

export type ValuesGetResponses = {
    /**
     * Successful operation
     */
    200: Array<{
        key?: string;
        value?: string;
    }>;
};

export type ValuesGetResponse = ValuesGetResponses[keyof ValuesGetResponses];

export type SuppressionsGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/suppressions';
};

export type SuppressionsGetErrors = {
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type SuppressionsGetResponses = {
    /**
     * Successful operation
     */
    200: {
        suppressions?: Array<SuppressionRecord>;
    };
};

export type SuppressionsGetResponse = SuppressionsGetResponses[keyof SuppressionsGetResponses];

export type SuppressionsUpsertData = {
    body: {
        suppression?: SuppressionInput;
    };
    path?: never;
    query?: never;
    url: '/suppressions';
};

export type SuppressionsUpsertErrors = {
    /**
     * Bad request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Internal server error
     */
    500: unknown;
    /**
     * Unexpected error
     */
    default: unknown;
};

export type SuppressionsUpsertResponses = {
    /**
     * Successful operation
     */
    200: {
        suppression?: SuppressionRecord;
    };
};

export type SuppressionsUpsertResponse = SuppressionsUpsertResponses[keyof SuppressionsUpsertResponses];
